import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Users,
  FileText,
  BarChart3,
  Shield,
  Check,
  X,
  DollarSign,
  Plus,
  Pencil,
  Trash2,
  Star,
  Clock,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  RotateCcw,
  Save,
  Download,
  Search,
  Briefcase,
  Settings,
  Layers,
  History,
  Copy,
  Send,
  Undo2,
  ToggleRight,
  ToggleLeft,
  UserPlus,
} from 'lucide-react';
import { AddUserModal } from '@/components/common/AddUserModal';
import {
  useUsers,
  useUpdateUser,
  useApproveUser,
  useDeleteUser,
  useAuditLogs,
  useSystemStats,
  getAuditLogsExportUrl,
  User,
} from '@/hooks/useAdmin';
import {
  useRateCards,
  useRateCard,
  useCreateRateCard,
  useUpdateRateCard,
  useSetRateCardEntries,
  useDeleteRateCard,
  useRateCardTypes,
  type RateCardType,
} from '@/hooks/useRateCards';
import {
  useWorkstreams,
  VcfWorkstream,
  VcfCapability,
  VcfActivity,
  VcfGuardrail,
} from '@/hooks/useWorkstreams';
import {
  useDeliveryProfiles,
  useDeliveryProfile,
  useCreateDeliveryProfile,
  useDeleteDeliveryProfile,
  useDuplicateDeliveryProfile,
  useProfileDeliveryPlan,
  useProfileVersions,
  useSetProfileCapabilities,
  useSaveProfileDeliveryPlan,
  useRestoreProfileVersion,
  useUpdateDeliveryProfile,
} from '@/hooks/useDeliveryProfiles';
import {
  useResourceRoles,
  useCreateResourceRole,
  useUpdateResourceRole,
  useDeleteResourceRole,
  type ResourceRole,
} from '@/hooks/useAdminRoles';
import {
  useSystemSettings,
  useUpdateSystemSettings,
  groupSettingsByCategory,
  type SystemSetting,
} from '@/hooks/useSystemSettings';
import {
  useCreateChangeSet,
  useFrameworkChangeSet,
  useFrameworkChangeSets,
  useSubmitChangeSet,
  useDiscardChangeSet,
  useRecordChange,
  buildPendingMap,
  generateEntityId,
  type FrameworkChangeItem,
} from '@/hooks/useFrameworkChanges';
import { useEditMode } from '@/contexts/EditModeContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';
import {
  PHASE_COLOURS as ADMIN_PHASE_COLOURS,
  getSkillColours as adminGetSkillColours,
  getRoleColours as adminGetRoleColours,
} from '@/components/delivery/constants';
import { generateWeekAllocations } from '@/lib/generateAllocations';
import { useSkills, useCreateSkill, useUpdateSkill, useDeleteSkill } from '@/hooks/useSkills';
import {
  activityToColumns,
  capabilityToColumns,
  workstreamToColumns,
  guardrailToColumns,
  useMemoMergedWorkstreams,
} from '@/components/admin/shared/entityHelpers';
import { CollapsibleMultipliers } from '@/components/admin/framework/CollapsibleMultipliers';
import { SubmitChangeSetModal } from '@/components/admin/framework/SubmitChangeSetModal';
import { WorkstreamModal } from '@/components/admin/framework/WorkstreamModal';
import { CapabilityModal } from '@/components/admin/framework/CapabilityModal';
import { MasterActivityModal } from '@/components/admin/framework/MasterActivityModal';
import { MasterGuardrailModal } from '@/components/admin/framework/MasterGuardrailModal';
import { ApprovalsSidePanel } from '@/components/admin/framework/ApprovalsSidePanel';
import { ChangeItemRow } from '@/components/admin/framework/ChangeItemRow';
import { VersionsSidePanel } from '@/components/admin/framework/VersionsSidePanel';
import { ActivityAllocationColumn } from '@/components/admin/framework/ActivityAllocationColumn';
import {
  useFeatureFlags,
  useCreateFeatureFlag,
  useUpdateFeatureFlag,
  useDeleteFeatureFlag,
  useSetFeatureFlagGroups,
  type FeatureFlag,
} from '@/hooks/useFeatureFlags';
import {
  useGroups,
  useGroup,
  useCreateGroup,
  useDeleteGroup,
  useSetGroupPermissions,
  useAddGroupMember,
  useRemoveGroupMember,
  usePermissionCatalogue,
} from '@/hooks/useGroups';
import { useAuth, useHasPermission } from '@/hooks/useAuth';
import {
  useGuardrailDefinitions,
  useCreateGuardrailDefinition,
  useUpdateGuardrailDefinition,
  useDeleteGuardrailDefinition,
  type GuardrailDefinition,
} from '@/hooks/useGuardrailDefinitions';

// Sub-tab IDs for grouped tabs
type ScopingSubTabId = 'workstreams' | 'guardrails' | 'roles' | 'skills' | 'rate-cards';
type AccessSubTabId = 'users' | 'groups' | 'feature-flags';

// Top-level tab IDs
type TopLevelTabId = 'scoping' | 'access' | 'settings' | 'stats' | 'logs';

// Combined state for active tab tracking
interface TabState {
  topLevel: TopLevelTabId;
  scopingSub: ScopingSubTabId;
  accessSub: AccessSubTabId;
}

// Top-level tabs configuration
const TOP_LEVEL_TABS: Array<{ id: TopLevelTabId; label: string; icon: typeof Users }> = [
  { id: 'scoping', label: 'Scoping Configuration', icon: Layers },
  { id: 'access', label: 'Access Control', icon: Shield },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'stats', label: 'System Stats', icon: BarChart3 },
  { id: 'logs', label: 'Audit Logs', icon: FileText },
];

// Sub-tabs for Scoping Configuration
const SCOPING_SUB_TABS: Array<{ id: ScopingSubTabId; label: string; icon: typeof Users }> = [
  { id: 'workstreams', label: 'Services Framework', icon: Layers },
  { id: 'guardrails', label: 'Guardrails', icon: Shield },
  { id: 'roles', label: 'Roles', icon: Briefcase },
  { id: 'skills', label: 'Skills', icon: Star },
  { id: 'rate-cards', label: 'Rate Cards', icon: DollarSign },
];

// Sub-tabs for Access Control
const ACCESS_SUB_TABS: Array<{ id: AccessSubTabId; label: string; icon: typeof Users }> = [
  { id: 'users', label: 'Users', icon: Users },
  { id: 'groups', label: 'Groups', icon: Shield },
  { id: 'feature-flags', label: 'Feature Flags', icon: ToggleRight },
];

export function Admin() {
  const [tabState, setTabState] = useState<TabState>({
    topLevel: 'scoping',
    scopingSub: 'workstreams',
    accessSub: 'users',
  });

  const setTopLevelTab = (id: TopLevelTabId) => {
    setTabState((prev) => ({ ...prev, topLevel: id }));
  };

  const setScopingSubTab = (id: ScopingSubTabId) => {
    setTabState((prev) => ({ ...prev, scopingSub: id }));
  };

  const setAccessSubTab = (id: AccessSubTabId) => {
    setTabState((prev) => ({ ...prev, accessSub: id }));
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between pb-4">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-xtravirt-dark">Admin</h1>
          <p className="mt-1 text-xs md:text-sm text-gray-600">
            Manage scoping configuration, users, and system settings
          </p>
        </div>
      </div>

      {/* Mobile: Section selector dropdown */}
      <div className="md:hidden mb-3">
        <select
          value={tabState.topLevel}
          onChange={(e) => setTopLevelTab(e.target.value as TopLevelTabId)}
          className="input w-full text-sm font-medium"
        >
          {TOP_LEVEL_TABS.map((tab) => (
            <option key={tab.id} value={tab.id}>{tab.label}</option>
          ))}
        </select>
      </div>

      {/* Desktop: Top-level Tabs */}
      <div className="hidden md:flex tabs">
        {TOP_LEVEL_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setTopLevelTab(tab.id)}
            className={cn(
              'tab',
              tabState.topLevel === tab.id && 'tab-active'
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sub-tabs for grouped sections - scrollable on mobile */}
      {tabState.topLevel === 'scoping' && (
        <div className="mt-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <div className="flex gap-1 border-b border-gray-200 px-1 min-w-max">
            {SCOPING_SUB_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setScopingSubTab(tab.id)}
                className={cn(
                  'flex items-center gap-1.5 px-2 md:px-3 py-2 text-xs md:text-sm font-medium transition-colors whitespace-nowrap',
                  tabState.scopingSub === tab.id
                    ? 'border-b-2 border-xtravirt-accent text-xtravirt-accent'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                <tab.icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {tabState.topLevel === 'access' && (
        <div className="mt-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <div className="flex gap-1 border-b border-gray-200 px-1 min-w-max">
            {ACCESS_SUB_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setAccessSubTab(tab.id)}
                className={cn(
                  'flex items-center gap-1.5 px-2 md:px-3 py-2 text-xs md:text-sm font-medium transition-colors whitespace-nowrap',
                  tabState.accessSub === tab.id
                    ? 'border-b-2 border-xtravirt-accent text-xtravirt-accent'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                <tab.icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto py-3 md:py-4">
        {/* Scoping Configuration sub-tabs */}
        {tabState.topLevel === 'scoping' && tabState.scopingSub === 'workstreams' && <MasterDataTab />}
        {tabState.topLevel === 'scoping' && tabState.scopingSub === 'guardrails' && <GuardrailDefinitionsTab />}
        {tabState.topLevel === 'scoping' && tabState.scopingSub === 'roles' && <RolesTab />}
        {tabState.topLevel === 'scoping' && tabState.scopingSub === 'skills' && <SkillsTab />}
        {tabState.topLevel === 'scoping' && tabState.scopingSub === 'rate-cards' && <RateCardsTab />}

        {/* Access Control sub-tabs */}
        {tabState.topLevel === 'access' && tabState.accessSub === 'users' && <UsersTab />}
        {tabState.topLevel === 'access' && tabState.accessSub === 'groups' && <GroupsTab />}
        {tabState.topLevel === 'access' && tabState.accessSub === 'feature-flags' && <FeatureFlagsTab />}

        {/* Standalone tabs */}
        {tabState.topLevel === 'settings' && <SettingsTab />}
        {tabState.topLevel === 'stats' && <StatsTab />}
        {tabState.topLevel === 'logs' && <AuditLogsTab />}
      </div>
    </div>
  );
}

// ============ Users Tab ============

type UpdateUserFn = (data: {
  id: string;
  status?: 'pending' | 'active' | 'inactive';
  isSuperAdmin?: boolean;
  groupIds?: string[];
}) => void;

interface UserRowProps {
  user: User;
  onUpdate: UpdateUserFn;
  onApprove: (data: { id: string; groupIds: string[] }) => void;
  onDelete: (id: string) => void;
  availableGroups: Array<{ id: string; code: string; name: string }>;
  currentUserId: string | undefined;
  canApprove: boolean;
  canDelete: boolean;
  canManageSuperAdmin: boolean;
  totalSuperAdmins: number;
}

function UsersTab() {
  const { data, isLoading, refetch } = useUsers();
  const { data: groupsData } = useGroups();
  const updateUser = useUpdateUser();
  const approveUser = useApproveUser();
  const deleteUser = useDeleteUser();
  const { user: currentUser } = useAuth();
  const canApprove = useHasPermission('admin.users.approve');
  const canDelete = useHasPermission('admin.users.delete');
  const canManageSuperAdmin = useHasPermission('admin.users.super_admin.manage');
  const [showAddModal, setShowAddModal] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const users = data?.users || [];
  const availableGroups = (groupsData?.groups || []).map((g) => ({
    id: g.id,
    code: g.code,
    name: g.name,
  }));

  const pendingCount = users.filter((u) => u.status === 'pending').length;
  const totalSuperAdmins = users.filter((u) => u.isSuperAdmin).length;

  const handleApprove = async (data: { id: string; groupIds: string[] }) => {
    try {
      await approveUser.mutateAsync(data);
    } catch (e) {
      alert((e as { message?: string }).message || 'Failed to approve user');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteUser.mutateAsync(id);
    } catch (e) {
      const err = e as { message?: string; details?: unknown };
      const detailsObj = err.details as
        | { assignments?: Array<{ entity: string; field: string; count: number }> }
        | undefined;
      if (detailsObj?.assignments && detailsObj.assignments.length > 0) {
        const list = detailsObj.assignments
          .map((a) => `  • ${a.entity}.${a.field}: ${a.count}`)
          .join('\n');
        alert(
          `Cannot delete — this user is still referenced by:\n${list}\n\nReassign these to another user first.`
        );
      } else {
        alert(err.message || 'Failed to delete user');
      }
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 md:mb-4 gap-2">
        <p className="text-xs md:text-sm text-gray-600">
          {users.length} users
          {pendingCount > 0 && (
            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
              {pendingCount} pending
            </span>
          )}
        </p>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary btn-sm flex items-center gap-1.5"
        >
          <UserPlus className="h-3.5 w-3.5" /> Add user
        </button>
      </div>

      {showAddModal && (
        <AddUserModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => {
            setShowAddModal(false);
            refetch();
          }}
        />
      )}

      {/* Mobile: Card view */}
      <div className="md:hidden space-y-3">
        {users.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            onUpdate={updateUser.mutate}
            onApprove={handleApprove}
            onDelete={handleDelete}
            availableGroups={availableGroups}
            currentUserId={currentUser?.id}
            canApprove={canApprove}
            canDelete={canDelete}
            canManageSuperAdmin={canManageSuperAdmin}
            totalSuperAdmins={totalSuperAdmins}
          />
        ))}
      </div>

      {/* Desktop: Table view */}
      <div className="hidden md:block card overflow-hidden !p-0">
        <table className="table">
          <thead>
            <tr>
              <th>User</th>
              <th>Groups</th>
              <th>Status</th>
              <th>Super Admin</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                onUpdate={updateUser.mutate}
                onApprove={handleApprove}
                onDelete={handleDelete}
                availableGroups={availableGroups}
                currentUserId={currentUser?.id}
                canApprove={canApprove}
                canDelete={canDelete}
                canManageSuperAdmin={canManageSuperAdmin}
                totalSuperAdmins={totalSuperAdmins}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Status badge shared by row + card
function StatusBadge({ status }: { status: 'pending' | 'active' | 'inactive' }) {
  if (status === 'pending') {
    return (
      <span className="badge badge-yellow">
        <Clock className="h-3 w-3" />
        Pending
      </span>
    );
  }
  if (status === 'inactive') {
    return (
      <span className="badge badge-red">
        <X className="h-3 w-3" />
        Inactive
      </span>
    );
  }
  return (
    <span className="badge badge-green">
      <Check className="h-3 w-3" />
      Active
    </span>
  );
}

// User Card for mobile view
function UserCard({
  user,
  onUpdate,
  onApprove,
  onDelete,
  availableGroups,
  currentUserId,
  canApprove,
  canDelete,
  canManageSuperAdmin,
  totalSuperAdmins,
}: UserRowProps) {
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [showApprove, setShowApprove] = useState(false);

  const isSelf = currentUserId === user.id;
  const lastSuperAdmin = user.isSuperAdmin && totalSuperAdmins <= 1;

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={user.avatarUrl || `https://ui-avatars.com/api/?name=${user.name}`}
            alt={user.name}
            className="h-10 w-10 rounded-full flex-shrink-0"
          />
          <div className="min-w-0">
            <p className="font-medium text-xtravirt-dark truncate">{user.name}</p>
            <p className="text-xs text-gray-500 truncate">{user.email}</p>
          </div>
        </div>
        <StatusBadge status={user.status} />
      </div>
      {/* Groups */}
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {user.groups && user.groups.length > 0 ? (
          user.groups.map((g) => (
            <span key={g.id} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
              {g.name}
            </span>
          ))
        ) : (
          <span className="text-xs text-gray-400">&mdash;</span>
        )}
        {user.status !== 'pending' && (
          <button
            onClick={() => setShowGroupPicker(!showGroupPicker)}
            className="text-xs text-blue-600 hover:text-blue-700 ml-1"
          >
            Edit
          </button>
        )}
      </div>
      {showGroupPicker && (
        <UserGroupPicker
          user={user}
          availableGroups={availableGroups}
          onSave={(groupIds) => onUpdate({ id: user.id, groupIds })}
          onClose={() => setShowGroupPicker(false)}
        />
      )}

      {/* Super Admin toggle */}
      <div className="mt-3 flex items-center justify-between text-xs">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={user.isSuperAdmin}
            disabled={!canManageSuperAdmin || isSelf || lastSuperAdmin}
            onChange={(e) => onUpdate({ id: user.id, isSuperAdmin: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-gray-300 text-xtravirt-cyan"
          />
          <span className="text-gray-700">Super Admin</span>
        </label>
        {lastSuperAdmin && (
          <span className="text-[10px] text-gray-400">last super admin</span>
        )}
      </div>

      {/* Actions */}
      <div className="mt-3 flex items-center justify-end gap-2">
        {user.status === 'pending' && canApprove && (
          <button
            onClick={() => setShowApprove(!showApprove)}
            className="btn-primary btn-sm text-xs"
          >
            Approve
          </button>
        )}
        {user.status === 'active' && !isSelf && (
          <button
            onClick={() => onUpdate({ id: user.id, status: 'inactive' })}
            className="btn-secondary text-xs py-1.5 px-3 text-red-600"
          >
            Deactivate
          </button>
        )}
        {user.status === 'inactive' && (
          <>
            <button
              onClick={() => onUpdate({ id: user.id, status: 'active' })}
              className="btn-secondary text-xs py-1.5 px-3 text-green-600"
            >
              Reactivate
            </button>
            {canDelete && !user.isSuperAdmin && (
              <button
                onClick={() => {
                  if (confirm(`Permanently delete ${user.name}? This cannot be undone.`)) {
                    onDelete(user.id);
                  }
                }}
                className="btn-secondary text-xs py-1.5 px-3 text-red-600"
              >
                Delete
              </button>
            )}
          </>
        )}
      </div>

      {showApprove && (
        <ApproveUserPicker
          user={user}
          availableGroups={availableGroups}
          onSave={(groupIds) => {
            onApprove({ id: user.id, groupIds });
            setShowApprove(false);
          }}
          onClose={() => setShowApprove(false)}
        />
      )}
    </div>
  );
}

// Group picker for updating an existing active user's memberships
function UserGroupPicker({
  user,
  availableGroups,
  onSave,
  onClose,
}: {
  user: User;
  availableGroups: Array<{ id: string; code: string; name: string }>;
  onSave: (groupIds: string[]) => void;
  onClose: () => void;
}) {
  const currentGroupIds = new Set((user.groups || []).map((g) => g.id));
  const [selected, setSelected] = useState<Set<string>>(new Set(currentGroupIds));

  const toggle = (groupId: string) => {
    const next = new Set(selected);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    setSelected(next);
  };

  const handleSave = () => {
    onSave(Array.from(selected));
    onClose();
  };

  return (
    <div className="mt-2 rounded border border-gray-200 bg-white p-2 shadow-sm space-y-1">
      {availableGroups.map((g) => (
        <label
          key={g.id}
          className="flex items-center gap-2 text-xs cursor-pointer py-0.5 px-1 hover:bg-gray-50 rounded"
        >
          <input
            type="checkbox"
            checked={selected.has(g.id)}
            onChange={() => toggle(g.id)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-xtravirt-cyan"
          />
          <span className="text-gray-700">{g.name}</span>
        </label>
      ))}
      <div className="flex justify-end gap-2 pt-1 border-t border-gray-100 mt-1">
        <button onClick={onClose} className="text-[10px] text-gray-500 hover:text-gray-700">
          Cancel
        </button>
        <button onClick={handleSave} className="text-[10px] text-blue-600 hover:text-blue-700 font-medium">
          Save
        </button>
      </div>
    </div>
  );
}

// Group picker for approving a pending user — requires at least one group
function ApproveUserPicker({
  user: _user,
  availableGroups,
  onSave,
  onClose,
}: {
  user: User;
  availableGroups: Array<{ id: string; code: string; name: string }>;
  onSave: (groupIds: string[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (groupId: string) => {
    const next = new Set(selected);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    setSelected(next);
  };

  const handleSave = () => {
    if (selected.size === 0) return;
    onSave(Array.from(selected));
  };

  return (
    <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 shadow-sm space-y-1">
      <p className="text-[10px] font-semibold text-amber-900 uppercase mb-1">
        Pick at least one group to approve
      </p>
      {availableGroups.map((g) => (
        <label
          key={g.id}
          className="flex items-center gap-2 text-xs cursor-pointer py-0.5 px-1 hover:bg-amber-100 rounded"
        >
          <input
            type="checkbox"
            checked={selected.has(g.id)}
            onChange={() => toggle(g.id)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-xtravirt-cyan"
          />
          <span className="text-gray-800">{g.name}</span>
        </label>
      ))}
      <div className="flex justify-end gap-2 pt-1 border-t border-amber-200 mt-1">
        <button onClick={onClose} className="text-[10px] text-gray-500 hover:text-gray-700">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={selected.size === 0}
          className="text-[10px] font-semibold text-green-700 hover:text-green-800 disabled:text-gray-300 disabled:cursor-not-allowed"
        >
          Approve
        </button>
      </div>
    </div>
  );
}

function UserRow({
  user,
  onUpdate,
  onApprove,
  onDelete,
  availableGroups,
  currentUserId,
  canApprove,
  canDelete,
  canManageSuperAdmin,
  totalSuperAdmins,
}: UserRowProps) {
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [showApprove, setShowApprove] = useState(false);

  const isSelf = currentUserId === user.id;
  const lastSuperAdmin = user.isSuperAdmin && totalSuperAdmins <= 1;

  return (
    <tr>
      <td>
        <div className="flex items-center gap-3">
          {user.avatarUrl ? (
            <img src={user.avatarUrl} alt={user.name} className="h-8 w-8 rounded-full" />
          ) : (
            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
              <Users className="h-4 w-4 text-gray-500" />
            </div>
          )}
          <div>
            <p className="font-medium text-sm text-xtravirt-dark">{user.name}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
        </div>
      </td>
      <td>
        <div className="relative">
          <div className="flex flex-wrap items-center gap-1">
            {user.groups && user.groups.length > 0 ? (
              user.groups.map((g) => (
                <span
                  key={g.id}
                  className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                >
                  {g.name}
                </span>
              ))
            ) : (
              <span className="text-gray-400">&mdash;</span>
            )}
            {user.status !== 'pending' && (
              <button
                onClick={() => setShowGroupPicker(!showGroupPicker)}
                className="ml-1 text-xs text-blue-600 hover:text-blue-700"
              >
                Edit
              </button>
            )}
          </div>
          {showGroupPicker && (
            <UserGroupPicker
              user={user}
              availableGroups={availableGroups}
              onSave={(groupIds) => onUpdate({ id: user.id, groupIds })}
              onClose={() => setShowGroupPicker(false)}
            />
          )}
          {showApprove && (
            <ApproveUserPicker
              user={user}
              availableGroups={availableGroups}
              onSave={(groupIds) => {
                onApprove({ id: user.id, groupIds });
                setShowApprove(false);
              }}
              onClose={() => setShowApprove(false)}
            />
          )}
        </div>
      </td>
      <td>
        <StatusBadge status={user.status} />
      </td>
      <td>
        <label
          className="flex items-center gap-2 text-xs"
          title={
            isSelf
              ? 'You cannot change your own super admin status'
              : lastSuperAdmin
                ? 'Cannot demote the last super admin'
                : !canManageSuperAdmin
                  ? 'Missing permission'
                  : ''
          }
        >
          <input
            type="checkbox"
            checked={user.isSuperAdmin}
            disabled={!canManageSuperAdmin || isSelf || lastSuperAdmin}
            onChange={(e) => onUpdate({ id: user.id, isSuperAdmin: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-gray-300 text-xtravirt-cyan disabled:cursor-not-allowed"
          />
          {user.isSuperAdmin && (
            <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700">
              Super
            </span>
          )}
        </label>
      </td>
      <td className="text-sm text-gray-500">
        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'}
      </td>
      <td>
        <div className="flex items-center gap-2">
          {user.status === 'pending' && canApprove && (
            <button
              onClick={() => setShowApprove(!showApprove)}
              className="text-xs font-medium text-green-600 hover:text-green-700"
            >
              Approve
            </button>
          )}
          {user.status === 'active' && !isSelf && (
            <button
              onClick={() => onUpdate({ id: user.id, status: 'inactive' })}
              className="text-xs font-medium text-red-600 hover:text-red-700"
            >
              Deactivate
            </button>
          )}
          {user.status === 'inactive' && (
            <>
              <button
                onClick={() => onUpdate({ id: user.id, status: 'active' })}
                className="text-xs font-medium text-green-600 hover:text-green-700"
              >
                Reactivate
              </button>
              {canDelete && !user.isSuperAdmin && (
                <button
                  onClick={() => {
                    if (confirm(`Permanently delete ${user.name}? This cannot be undone.`)) {
                      onDelete(user.id);
                    }
                  }}
                  className="text-xs font-medium text-red-600 hover:text-red-700"
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ============ Stats Tab ============

function StatsTab() {
  const { data, isLoading } = useSystemStats();

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const stats = [
    { label: 'Users', value: data?.users || 0, icon: Users, color: 'bg-purple-50 text-purple-600' },
    { label: 'Customers', value: data?.customers || 0, icon: Users, color: 'bg-blue-50 text-blue-600' },
    { label: 'Opportunities', value: data?.opportunities || 0, icon: BarChart3, color: 'bg-green-50 text-green-600' },
    { label: 'Projects', value: data?.projects || 0, icon: FileText, color: 'bg-orange-50 text-orange-600' },
    { label: 'TAR Plans', value: data?.tarPlans || 0, icon: FileText, color: 'bg-cyan-50 text-cyan-600' },
    { label: 'Workstreams', value: data?.workstreams || 0, icon: Shield, color: 'bg-indigo-50 text-indigo-600' },
    { label: 'Capabilities', value: data?.capabilities || 0, icon: Shield, color: 'bg-pink-50 text-pink-600' },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat) => (
        <div key={stat.label} className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">{stat.label}</span>
            <div className={cn('stat-card-icon', stat.color)}>
              <stat.icon className="h-5 w-5" />
            </div>
          </div>
          <div className="stat-card-value">{stat.value}</div>
        </div>
      ))}
    </div>
  );
}

// ============ Audit Logs Tab ============

function AuditLogsTab() {
  const [filters, setFilters] = useState({
    entityType: '',
    action: '' as '' | 'create' | 'update' | 'delete' | 'archive' | 'restore',
    userEmail: '',
    startDate: '',
    endDate: '',
  });
  const [page, setPage] = useState(0);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const limit = 25;

  const { data, isLoading } = useAuditLogs({
    entityType: filters.entityType || undefined,
    action: filters.action || undefined,
    userEmail: filters.userEmail || undefined,
    startDate: filters.startDate || undefined,
    endDate: filters.endDate || undefined,
    limit,
    offset: page * limit,
  });

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const toggleExpand = useCallback((logId: string) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) {
        next.delete(logId);
      } else {
        next.add(logId);
      }
      return next;
    });
  }, []);

  const handleExport = () => {
    const url = getAuditLogsExportUrl({
      entityType: filters.entityType || undefined,
      action: filters.action || undefined,
      userEmail: filters.userEmail || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    });
    window.open(url, '_blank');
  };

  const parseChanges = (changesStr: string | null): Record<string, { old?: unknown; new?: unknown }> | null => {
    if (!changesStr) return null;
    try {
      return JSON.parse(changesStr);
    } catch {
      return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="label text-xs">Entity Type</label>
            <select
              value={filters.entityType}
              onChange={(e) => { setFilters(f => ({ ...f, entityType: e.target.value })); setPage(0); }}
              className="input mt-1 w-full"
            >
              <option value="">All Types</option>
              <option value="project">Projects</option>
              <option value="opportunity">Opportunities</option>
              <option value="customer">Customers</option>
              <option value="tar_plan">TAR Plans</option>
              <option value="user">Users</option>
              <option value="rate_card">Rate Cards</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">Action</label>
            <select
              value={filters.action}
              onChange={(e) => { setFilters(f => ({ ...f, action: e.target.value as typeof filters.action })); setPage(0); }}
              className="input mt-1 w-full"
            >
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="archive">Archive</option>
              <option value="restore">Restore</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">User Email</label>
            <div className="relative mt-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={filters.userEmail}
                onChange={(e) => { setFilters(f => ({ ...f, userEmail: e.target.value })); setPage(0); }}
                className="input w-full pl-8"
              />
            </div>
          </div>
          <div>
            <label className="label text-xs">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => { setFilters(f => ({ ...f, startDate: e.target.value })); setPage(0); }}
              className="input mt-1 w-full"
            />
          </div>
          <div>
            <label className="label text-xs">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => { setFilters(f => ({ ...f, endDate: e.target.value })); setPage(0); }}
              className="input mt-1 w-full"
            />
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <span className="text-sm text-gray-500">{total} total logs</span>
          <button
            onClick={handleExport}
            className="btn-secondary btn-sm flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex h-32 items-center justify-center">
          <LoadingSpinner />
        </div>
      )}

      {/* Logs Table */}
      {!isLoading && (
        <div className="card overflow-hidden !p-0">
          {logs.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">No audit logs found</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Entity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => {
                  const isExpanded = expandedLogs.has(log.id);
                  const changes = parseChanges(log.changes);
                  const hasChanges = changes && Object.keys(changes).length > 0;

                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className={cn(
                          'hover:bg-gray-50 transition-colors',
                          hasChanges && 'cursor-pointer'
                        )}
                        onClick={() => hasChanges && toggleExpand(log.id)}
                      >
                        <td className="px-2 text-center">
                          {hasChanges && (
                            isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-gray-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-gray-400" />
                            )
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'badge',
                              log.action === 'create' && 'badge-green',
                              log.action === 'update' && 'badge-blue',
                              log.action === 'delete' && 'badge-red',
                              log.action === 'archive' && 'badge-gray',
                              log.action === 'restore' && 'badge-cyan'
                            )}
                          >
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{log.entityType}</p>
                          <p className="text-xs text-gray-500 font-mono">{log.entityId.slice(0, 12)}...</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-700">{log.userEmail || 'System'}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                      </tr>
                      {/* Expanded changes row */}
                      {isExpanded && changes && (
                        <tr className="bg-gray-50">
                          <td></td>
                          <td colSpan={4} className="px-4 py-3">
                            <div className="text-sm">
                              <p className="font-medium text-gray-700 mb-2">Changes:</p>
                              <div className="space-y-1 font-mono text-xs">
                                {Object.entries(changes).map(([key, value]) => (
                                  <div key={key} className="flex items-start gap-2">
                                    <span className="text-gray-500 min-w-[120px]">{key}:</span>
                                    {value.old !== undefined && (
                                      <span className="text-red-600 line-through">
                                        {JSON.stringify(value.old)}
                                      </span>
                                    )}
                                    {value.new !== undefined && (
                                      <span className="text-green-600">
                                        {JSON.stringify(value.new)}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="btn-secondary btn-sm flex items-center gap-1 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="btn-secondary btn-sm flex items-center gap-1 disabled:opacity-50"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============ Rate Cards Tab ============

function RateCardsTab() {
  const { data, isLoading } = useRateCards(true);
  const { data: typesData, isLoading: typesLoading } = useRateCardTypes();
  const createRateCard = useCreateRateCard();
  const updateRateCard = useUpdateRateCard();
  const deleteRateCard = useDeleteRateCard();
  const { isEditMode } = useEditMode();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);

  if (isLoading || typesLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const rateCards = data?.rateCards || [];
  const rateCardTypes = typesData?.types || [];

  // Create a map of type IDs to types for quick lookup
  const typeMap = new Map(rateCardTypes.map((t) => [t.id, t]));

  const handleCreate = async (name: string, currency: 'GBP' | 'USD' | 'EUR', typeId: string) => {
    await createRateCard.mutateAsync({ name, currency, typeId });
    setShowCreateModal(false);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this rate card?')) {
      await deleteRateCard.mutateAsync(id);
    }
  };

  const handleSetDefault = async (id: string) => {
    await updateRateCard.mutateAsync({ id, isDefault: true });
  };

  return (
    <div>
      {!isEditMode && (
        <p className="text-xs text-gray-500 mb-3">
          Read-only — click Edit Services Framework to make changes
        </p>
      )}
      <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600">{rateCards.length} rate cards</p>
            {isEditMode && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                New Rate Card
              </button>
            )}
          </div>

          {editingCardId ? (
            <RateCardEditor
              rateCardId={editingCardId}
              onClose={() => setEditingCardId(null)}
            />
          ) : (
            <>
            {/* Mobile cards */}
            <div className="md:hidden card overflow-hidden !p-0 divide-y divide-gray-100">
              {rateCards.map((card) => {
                const cardType = card.typeId ? typeMap.get(card.typeId) : null;
                return (
                  <div key={card.id} className={cn("p-3 flex items-start justify-between gap-3", !card.isActive && "opacity-50")} onClick={() => isEditMode && setEditingCardId(card.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">{card.name}</div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        {cardType && <span>{cardType.name}</span>}
                        <span>{card.currency}</span>
                        {card.isDefault && <span className="text-amber-500 font-medium">&#9733; Default</span>}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0 mt-1" />
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Currency</th>
                    <th>Exchange Rate</th>
                    <th>Default</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rateCards.map((card) => {
                    const cardType = card.typeId ? typeMap.get(card.typeId) : null;
                    return (
                      <tr key={card.id} className={cn(!card.isActive && 'opacity-60')}>
                        <td className="font-medium">{card.name}</td>
                        <td>
                          {cardType ? (
                            <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-xtravirt-accent/10 text-xtravirt-accent">
                              {cardType.name}
                            </span>
                          ) : (
                            <span className="text-gray-400">&mdash;</span>
                          )}
                        </td>
                        <td>{card.currency}</td>
                        <td>{card.exchangeRate ? card.exchangeRate.toFixed(4) : '—'}</td>
                        <td>
                          {card.isDefault ? (
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          ) : null}
                        </td>
                        <td>
                          {card.isActive ? (
                            <span className="badge badge-green text-xs">Active</span>
                          ) : (
                            <span className="badge badge-gray text-xs">Inactive</span>
                          )}
                        </td>
                        <td className="text-right">
                          {isEditMode && (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setEditingCardId(card.id)}
                              className="btn-secondary text-xs px-2 py-1 flex items-center gap-1"
                            >
                              <Pencil className="h-3 w-3" />
                              Edit Rates
                            </button>
                            {!card.isDefault && card.isActive && (
                              <button
                                onClick={() => handleSetDefault(card.id)}
                                className="btn-secondary text-xs px-2 py-1 flex items-center gap-1"
                              >
                                <Star className="h-3 w-3" />
                                Set Default
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(card.id)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}

          {/* Create Modal */}
          {showCreateModal && (
            <div className="dialog-overlay">
              <div className="dialog">
                <div className="dialog-header">
                  <h2 className="dialog-title">Create Rate Card</h2>
                </div>
                <div className="dialog-body">
                  <CreateRateCardForm
                    rateCardTypes={rateCardTypes}
                    onSubmit={handleCreate}
                    onCancel={() => setShowCreateModal(false)}
                    isSubmitting={createRateCard.isPending}
                  />
                </div>
              </div>
            </div>
          )}
    </div>
  );
}

function CreateRateCardForm({
  rateCardTypes,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  rateCardTypes: RateCardType[];
  onSubmit: (name: string, currency: 'GBP' | 'USD' | 'EUR', typeId: string) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<'GBP' | 'USD' | 'EUR'>('GBP');
  const [typeId, setTypeId] = useState(rateCardTypes[0]?.id || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(name, currency, typeId);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4">
        <div>
          <label className="label">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Standard 2024"
            className="input mt-1 w-full"
            required
          />
        </div>
        <div>
          <label className="label">Rate Card Type</label>
          <select
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            className="input mt-1 w-full"
            required
          >
            <option value="">Select a type...</option>
            {rateCardTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
          {typeId && (
            <p className="text-xs text-gray-500 mt-1">
              {rateCardTypes.find((t) => t.id === typeId)?.description}
            </p>
          )}
        </div>
        <div>
          <label className="label">Currency</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as 'GBP' | 'USD' | 'EUR')}
            className="input mt-1 w-full"
          >
            <option value="GBP">GBP</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-6">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={!name || !typeId || isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create'}
        </button>
      </div>
    </form>
  );
}

function RateCardEditor({
  rateCardId,
  onClose,
}: {
  rateCardId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useRateCard(rateCardId);
  const { data: rolesData } = useResourceRoles();
  const setEntries = useSetRateCardEntries();

  // Entry state: role -> columnId -> value (in cents)
  const [entries, setLocalEntries] = useState<
    Map<string, { roleId?: string; role: string; values: Map<string, number> }>
  >(new Map());
  const [hasChanges, setHasChanges] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Get columns from the type
  const columns = data?.type?.columns || [];
  const rateCardRoles = rolesData?.roles || [];

  // Initialize entries from data when loaded
  React.useEffect(() => {
    if (data && !initialized && rateCardRoles.length > 0) {
      const newEntries = new Map<
        string,
        { roleId?: string; role: string; values: Map<string, number> }
      >();

      // Create entries for each rate card role
      for (const rateCardRole of rateCardRoles) {
        const existingEntry = data.entries.find((e) => e.roleId === rateCardRole.id || e.role === rateCardRole.name);
        const valuesMap = new Map<string, number>();

        // Initialize all columns with values from existing entry or 0
        for (const col of columns) {
          const existingValue = existingEntry?.values?.find((v) => v.columnId === col.id);
          valuesMap.set(col.id, existingValue?.value || 0);
        }

        newEntries.set(rateCardRole.id, {
          roleId: rateCardRole.id,
          role: rateCardRole.name,
          values: valuesMap,
        });
      }

      setLocalEntries(newEntries);
      setInitialized(true);
    }
  }, [data, rateCardRoles, columns, initialized]);

  const handleValueChange = (roleId: string, columnId: string, value: number) => {
    setLocalEntries((prev) => {
      const newMap = new Map(prev);
      const entry = newMap.get(roleId);
      if (entry) {
        const newValues = new Map(entry.values);
        newValues.set(columnId, value);
        newMap.set(roleId, { ...entry, values: newValues });
      }
      return newMap;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Convert Map to array format for API
    const entriesArray = Array.from(entries.values()).map((entry) => ({
      roleId: entry.roleId,
      role: entry.role,
      values: Array.from(entry.values.entries()).map(([columnId, value]) => ({
        columnId,
        value,
      })),
    }));

    // Filter out entries where all values are 0
    const nonEmptyEntries = entriesArray.filter((e) =>
      e.values.some((v) => v.value > 0)
    );

    await setEntries.mutateAsync({
      rateCardId,
      entries: nonEmptyEntries,
    });
    setHasChanges(false);
  };

  if (isLoading || !initialized) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-xtravirt-dark">
            Edit: {data?.rateCard.name}
          </h3>
          <button onClick={onClose} className="btn-secondary">Back</button>
        </div>
        <p className="text-sm text-gray-500">
          This rate card has no type assigned. Please update the rate card and assign a type before editing rates.
        </p>
      </div>
    );
  }

  // Sort columns by sort order
  const sortedColumns = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-xtravirt-dark">
            Edit: {data?.rateCard.name}
          </h3>
          {data?.type && (
            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-xtravirt-accent/10 text-xtravirt-accent">
              {data.type.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || setEntries.isPending}
            className="btn-primary"
          >
            {setEntries.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="table">
          <thead>
            <tr>
              <th>Role</th>
              {sortedColumns.map((col) => (
                <React.Fragment key={col.id}>
                  <th>
                    <div className="flex flex-col">
                      <span>{col.label}</span>
                      <span className="text-xs font-normal text-gray-400">
                        ({col.rateUnit})
                        {col.isCustomerFacing && ' • Customer'}
                        {col.isInternalCost && ' • Internal'}
                      </span>
                    </div>
                  </th>
                  {col.rateUnit === 'hourly' && (
                    <th>
                      <div className="flex flex-col">
                        <span>Daily Equiv.</span>
                        <span className="text-xs font-normal text-gray-400">(auto)</span>
                      </div>
                    </th>
                  )}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {rateCardRoles.map((rateCardRole) => {
              const entry = entries.get(rateCardRole.id);
              if (!entry) return null;

              return (
                <tr key={rateCardRole.id}>
                  <td className="font-medium">{rateCardRole.name}</td>
                  {sortedColumns.map((col) => {
                    const rawValue = entry.values.get(col.id) || 0;
                    const displayValue = rawValue / 100;
                    const currencySymbol = data?.rateCard.currency === 'GBP' ? '£' : data?.rateCard.currency === 'USD' ? '$' : '€';
                    const hoursPerDay = data?.rateCard.hoursPerDay || 8;

                    return (
                      <React.Fragment key={col.id}>
                        <td>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-400">{currencySymbol}</span>
                            <input
                              type="number"
                              value={displayValue}
                              onChange={(e) =>
                                handleValueChange(
                                  rateCardRole.id,
                                  col.id,
                                  Math.round(parseFloat(e.target.value || '0') * 100)
                                )
                              }
                              className="input w-24 text-sm"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </td>
                        {col.rateUnit === 'hourly' && (
                          <td>
                            <span className="text-sm text-gray-500">
                              {currencySymbol}{(displayValue * hoursPerDay).toFixed(2)}
                            </span>
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-gray-500">
        Enter rates in {data?.rateCard.currency}. Values are stored in cents for precision.
      </p>
    </div>
  );
}

// ============ Roles Tab ============

function RolesTab() {
  const { data, isLoading } = useResourceRoles(true); // Include inactive
  const createRole = useCreateResourceRole();
  const updateRole = useUpdateResourceRole();
  const deleteRole = useDeleteResourceRole();
  const canManage = useHasPermission('framework.skills.manage');
  const { isEditMode } = useEditMode();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    colorBg: '#F3F4F6',
    colorText: '#374151',
    sortOrder: 0,
    isActive: true,
  });

  const roles = data?.roles || [];

  const resetForm = useCallback(() => {
    setFormData({ code: '', name: '', description: '', colorBg: '#F3F4F6', colorText: '#374151', sortOrder: 0, isActive: true });
    setEditingId(null);
    setShowForm(false);
  }, []);

  const handleEdit = useCallback((role: ResourceRole) => {
    setFormData({
      code: role.code,
      name: role.name,
      description: role.description || '',
      colorBg: role.colorBg || '#F3F4F6',
      colorText: role.colorText || '#374151',
      sortOrder: role.sortOrder,
      isActive: role.isActive,
    });
    setEditingId(role.id);
    setShowForm(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      if (editingId) {
        await updateRole.mutateAsync({ id: editingId, ...formData });
      } else {
        await createRole.mutateAsync(formData);
      }
      resetForm();
    } catch (error) {
      console.error('Failed to save role:', error);
    }
  }, [editingId, formData, updateRole, createRole, resetForm]);

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('Are you sure you want to delete this role?')) return;
    try {
      await deleteRole.mutateAsync(id);
    } catch (error: any) {
      alert(error.message || 'Failed to delete role');
    }
  }, [deleteRole]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isEditMode && (
        <p className="text-xs text-gray-500">
          Read-only — click Edit Services Framework to make changes
        </p>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            {roles.length} role{roles.length !== 1 ? 's' : ''} configured
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Roles are used for activity assignments and rate card pricing
          </p>
        </div>
        {!showForm && canManage && isEditMode && (
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4" />
            Add Role
          </button>
        )}
      </div>

      {/* Inline Form */}
      {showForm && canManage && isEditMode && (
        <div className="card space-y-4">
          <h3 className="font-medium text-gray-900">
            {editingId ? 'Edit Role' : 'New Role'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="input"
                placeholder="SENIOR_ARCHITECT"
              />
              <p className="mt-1 text-xs text-gray-500">Unique identifier (uppercase)</p>
            </div>
            <div>
              <label className="label">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className="input"
                placeholder="Senior Architect"
              />
            </div>
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              className="input"
              rows={2}
              placeholder="Role responsibilities and typical activities"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Background Colour</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={formData.colorBg}
                  onChange={(e) => setFormData((prev) => ({ ...prev, colorBg: e.target.value }))}
                  className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.colorBg}
                  onChange={(e) => setFormData((prev) => ({ ...prev, colorBg: e.target.value }))}
                  className="input flex-1 font-mono text-xs"
                />
              </div>
            </div>
            <div>
              <label className="label">Text Colour</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={formData.colorText}
                  onChange={(e) => setFormData((prev) => ({ ...prev, colorText: e.target.value }))}
                  className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.colorText}
                  onChange={(e) => setFormData((prev) => ({ ...prev, colorText: e.target.value }))}
                  className="input flex-1 font-mono text-xs"
                />
              </div>
            </div>
            <div>
              <label className="label">Preview</label>
              <div className="mt-1 flex items-center justify-center h-9 rounded border border-gray-200">
                <span
                  className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                  style={{ backgroundColor: formData.colorBg, color: formData.colorText }}
                >
                  {formData.name || 'Role Name'}
                </span>
              </div>
            </div>
          </div>

          {editingId && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Status</label>
                <select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.value === 'active' }))}
                  className="input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={resetForm} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!formData.code || !formData.name || createRole.isPending || updateRole.isPending}
              className="btn-primary"
            >
              <Save className="h-4 w-4" />
              {editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {/* Roles Table */}
      {roles.length > 0 && (
        <>
        {/* Mobile cards */}
        <div className="md:hidden card overflow-hidden !p-0 divide-y divide-gray-100">
          {roles.map((role) => (
            <div key={role.id} className={cn("p-3 flex items-start justify-between gap-3", !role.isActive && "opacity-50")}>
              <div className="flex-1 min-w-0">
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase" style={{ backgroundColor: role.colorBg || '#F3F4F6', color: role.colorText || '#374151' }}>{role.name}</span>
                <div className="font-mono text-[10px] text-gray-500 mt-1">{role.code}</div>
                {role.description && <div className="text-xs text-gray-500 mt-1">{role.description}</div>}
              </div>
              {canManage && isEditMode && (
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => handleEdit(role)} className="p-1.5 rounded bg-gray-100 text-gray-500"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => handleDelete(role.id)} className="p-1.5 rounded bg-gray-100 text-gray-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="hidden md:block card overflow-hidden !p-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Description</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {roles.map((role) => (
                <tr key={role.id} className={cn(!role.isActive && 'bg-gray-50 opacity-60')}>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600">{role.code}</td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                      style={{ backgroundColor: role.colorBg || '#F3F4F6', color: role.colorText || '#374151' }}
                    >
                      {role.name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                    {role.description || '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                      role.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    )}>
                      {role.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canManage && isEditMode && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(role)}
                          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(role.id)}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {roles.length === 0 && !showForm && (
        <div className="text-center py-12 text-gray-500">
          <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No roles configured</p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-secondary mt-4"
          >
            Add First Role
          </button>
        </div>
      )}
    </div>
  );
}

// ============ Guardrail Definitions Tab ============

function GuardrailDefinitionsTab() {
  const { data, isLoading } = useGuardrailDefinitions(true);
  const createDefinition = useCreateGuardrailDefinition();
  const updateDefinition = useUpdateGuardrailDefinition();
  const deleteDefinition = useDeleteGuardrailDefinition();
  const canManage = useHasPermission('framework.guardrails.manage');
  const { isEditMode } = useEditMode();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    inputType: 'number' as 'number' | 'select' | 'boolean',
    optionsText: '',
    minValue: '',
    maxValue: '',
    sortOrder: 0,
    isActive: true,
  });

  const definitions = data?.definitions || [];

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      inputType: 'number',
      optionsText: '',
      minValue: '',
      maxValue: '',
      sortOrder: 0,
      isActive: true,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (def: GuardrailDefinition) => {
    setFormData({
      code: def.code,
      name: def.name,
      description: def.description || '',
      inputType: def.inputType,
      optionsText: def.options?.join(', ') || '',
      minValue: def.minValue?.toString() || '',
      maxValue: def.maxValue?.toString() || '',
      sortOrder: def.sortOrder,
      isActive: def.isActive,
    });
    setEditingId(def.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      const parsedOptions =
        formData.inputType === 'select' && formData.optionsText
          ? formData.optionsText
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : undefined;

      const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description || null,
        inputType: formData.inputType,
        options: parsedOptions || null,
        minValue: formData.minValue ? parseInt(formData.minValue) : null,
        maxValue: formData.maxValue ? parseInt(formData.maxValue) : null,
        sortOrder: formData.sortOrder,
        isActive: formData.isActive,
      };

      if (editingId) {
        await updateDefinition.mutateAsync({ id: editingId, ...payload });
      } else {
        await createDefinition.mutateAsync(payload);
      }
      resetForm();
    } catch (error: unknown) {
      const err = error as { message?: string };
      alert(err.message || 'Failed to save guardrail definition');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this guardrail definition?')) return;
    try {
      await deleteDefinition.mutateAsync(id);
    } catch (error: unknown) {
      const err = error as { message?: string };
      alert(err.message || 'Failed to delete guardrail definition. It may be in use by guardrail usages.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isEditMode && (
        <p className="text-xs text-gray-500">
          Read-only — click Edit Services Framework to make changes
        </p>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            {definitions.length} guardrail definition{definitions.length !== 1 ? 's' : ''} configured
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Guardrail definitions describe the &quot;what&quot; -- the master list of guardrails available for use in capabilities and global scopes
          </p>
        </div>
        {!showForm && canManage && isEditMode && (
          <button onClick={() => setShowForm(true)} className="btn-primary">
            <Plus className="h-4 w-4" />
            Add Definition
          </button>
        )}
      </div>

      {showForm && canManage && isEditMode && (
        <div className="card space-y-4">
          <h3 className="font-medium text-gray-900">
            {editingId ? 'Edit Guardrail Definition' : 'New Guardrail Definition'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="input"
                placeholder="DC_COUNT"
              />
              <p className="mt-1 text-xs text-gray-500">Unique identifier (uppercase)</p>
            </div>
            <div>
              <label className="label">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className="input"
                placeholder="Number of Data Centres"
              />
            </div>
            <div>
              <label className="label">Input Type</label>
              <select
                value={formData.inputType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    inputType: e.target.value as 'number' | 'select' | 'boolean',
                  }))
                }
                className="input"
              >
                <option value="number">Number</option>
                <option value="select">Select</option>
                <option value="boolean">Boolean</option>
              </select>
            </div>
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              className="input"
              rows={2}
              placeholder="Optional description"
            />
          </div>

          {formData.inputType === 'select' && (
            <div>
              <label className="label">Options (comma-separated)</label>
              <input
                type="text"
                value={formData.optionsText}
                onChange={(e) => setFormData((prev) => ({ ...prev, optionsText: e.target.value }))}
                className="input"
                placeholder="Standard, Complex, Regulated"
              />
            </div>
          )}

          {formData.inputType === 'number' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Min Value</label>
                <input
                  type="number"
                  value={formData.minValue}
                  onChange={(e) => setFormData((prev) => ({ ...prev, minValue: e.target.value }))}
                  className="input"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="label">Max Value</label>
                <input
                  type="number"
                  value={formData.maxValue}
                  onChange={(e) => setFormData((prev) => ({ ...prev, maxValue: e.target.value }))}
                  className="input"
                  placeholder="100"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Sort Order</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData((prev) => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                className="input"
                min="0"
              />
            </div>
            {editingId && (
              <div>
                <label className="label">Status</label>
                <select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, isActive: e.target.value === 'active' }))
                  }
                  className="input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={resetForm} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!formData.code || !formData.name || createDefinition.isPending || updateDefinition.isPending}
              className="btn-primary flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {definitions.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-2 text-sm text-gray-500">No guardrail definitions configured yet.</p>
          {canManage && isEditMode && (
            <button
              onClick={() => setShowForm(true)}
              className="btn-secondary mt-4"
            >
              Add First Definition
            </button>
          )}
        </div>
      ) : (
        <>
        {/* Mobile cards */}
        <div className="md:hidden card overflow-hidden !p-0 divide-y divide-gray-100">
          {definitions.map((def) => (
            <div key={def.id} className="p-3 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-sm">{def.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono text-[10px] text-xtravirt-cyan">{def.code}</span>
                  <span className={cn('text-[9px] px-1.5 py-0.5 rounded font-bold uppercase',
                    def.inputType === 'number' && 'bg-blue-100 text-blue-700',
                    def.inputType === 'select' && 'bg-purple-100 text-purple-700',
                    def.inputType === 'boolean' && 'bg-amber-100 text-amber-700',
                  )}>{def.inputType}</span>
                  <span className={cn('text-[9px] px-1.5 py-0.5 rounded',
                    def.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  )}>{def.isActive ? 'Active' : 'Inactive'}</span>
                </div>
                {def.description && <div className="text-xs text-gray-500 mt-1 truncate">{def.description}</div>}
              </div>
              {canManage && isEditMode && (
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => handleEdit(def)} className="p-1.5 rounded bg-gray-100 text-gray-500"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => handleDelete(def.id)} className="p-1.5 rounded bg-gray-100 text-gray-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="hidden md:block card overflow-hidden !p-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  Input Type
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">
                  Constraints
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">
                  Order
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">
                  Status
                </th>
                {canManage && isEditMode && (
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {definitions.map((def) => (
                <tr
                  key={def.id}
                  className={cn(!def.isActive && 'bg-gray-50 opacity-60')}
                >
                  <td className="px-4 py-3 font-mono text-sm text-gray-600">{def.code}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{def.name}</div>
                    {def.description && (
                      <div className="text-xs text-gray-500">{def.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        def.inputType === 'number' && 'bg-blue-100 text-blue-700',
                        def.inputType === 'select' && 'bg-purple-100 text-purple-700',
                        def.inputType === 'boolean' && 'bg-amber-100 text-amber-700'
                      )}
                    >
                      {def.inputType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-500">
                    {def.inputType === 'number' && (
                      <span>
                        {def.minValue != null && `min: ${def.minValue}`}
                        {def.minValue != null && def.maxValue != null && ', '}
                        {def.maxValue != null && `max: ${def.maxValue}`}
                        {def.minValue == null && def.maxValue == null && '--'}
                      </span>
                    )}
                    {def.inputType === 'select' && (
                      <span title={def.options?.join(', ')}>
                        {def.options?.length || 0} option{(def.options?.length || 0) !== 1 ? 's' : ''}
                      </span>
                    )}
                    {def.inputType === 'boolean' && <span>--</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-500">{def.sortOrder}</td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                        def.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {def.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {canManage && isEditMode && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(def)}
                          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(def.id)}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

// ============ Skills Tab ============

function SkillsTab() {
  const { data, isLoading } = useSkills(true);
  const createSkill = useCreateSkill();
  const updateSkill = useUpdateSkill();
  const deleteSkill = useDeleteSkill();
  const canManageSkills = useHasPermission('framework.skills.manage');
  const { isEditMode } = useEditMode();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    colorBg: '#F3F4F6',
    colorText: '#374151',
    sortOrder: 0,
    isActive: true,
  });

  const skills = data?.skills || [];

  const resetForm = () => {
    setFormData({ code: '', name: '', description: '', colorBg: '#F3F4F6', colorText: '#374151', sortOrder: 0, isActive: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (skill: { id: string; code: string; name: string; description: string | null; colorBg: string; colorText: string; sortOrder: number; isActive: boolean }) => {
    setFormData({
      code: skill.code,
      name: skill.name,
      description: skill.description || '',
      colorBg: skill.colorBg,
      colorText: skill.colorText,
      sortOrder: skill.sortOrder,
      isActive: skill.isActive,
    });
    setEditingId(skill.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await updateSkill.mutateAsync({ id: editingId, ...formData });
      } else {
        await createSkill.mutateAsync(formData);
      }
      resetForm();
    } catch (error) {
      console.error('Failed to save skill:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this skill?')) return;
    try {
      await deleteSkill.mutateAsync(id);
    } catch (error: unknown) {
      const err = error as { message?: string };
      alert(err.message || 'Failed to delete skill');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isEditMode && (
        <p className="text-xs text-gray-500">
          Read-only — click Edit Services Framework to make changes
        </p>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            {skills.length} skill{skills.length !== 1 ? 's' : ''} configured
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Skills are assigned to activities and shown as colour-coded badges
          </p>
        </div>
        {!showForm && canManageSkills && isEditMode && (
          <button onClick={() => setShowForm(true)} className="btn-primary">
            <Plus className="h-4 w-4" />
            Add Skill
          </button>
        )}
      </div>

      {showForm && canManageSkills && isEditMode && (
        <div className="card space-y-4">
          <h3 className="font-medium text-gray-900">
            {editingId ? 'Edit Skill' : 'New Skill'}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Code</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className="input"
                placeholder="VCF"
              />
              <p className="mt-1 text-xs text-gray-500">Unique identifier (uppercase)</p>
            </div>
            <div>
              <label className="label">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className="input"
                placeholder="VCF"
              />
            </div>
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              className="input"
              rows={2}
              placeholder="Optional description"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Background Colour</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={formData.colorBg}
                  onChange={(e) => setFormData((prev) => ({ ...prev, colorBg: e.target.value }))}
                  className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.colorBg}
                  onChange={(e) => setFormData((prev) => ({ ...prev, colorBg: e.target.value }))}
                  className="input flex-1 font-mono text-xs"
                />
              </div>
            </div>
            <div>
              <label className="label">Text Colour</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={formData.colorText}
                  onChange={(e) => setFormData((prev) => ({ ...prev, colorText: e.target.value }))}
                  className="h-9 w-12 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.colorText}
                  onChange={(e) => setFormData((prev) => ({ ...prev, colorText: e.target.value }))}
                  className="input flex-1 font-mono text-xs"
                />
              </div>
            </div>
            <div>
              <label className="label">Preview</label>
              <div className="mt-1 flex items-center justify-center h-9 rounded border border-gray-200">
                <span
                  className="px-2.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                  style={{ backgroundColor: formData.colorBg, color: formData.colorText }}
                >
                  {formData.name || 'Skill'}
                </span>
              </div>
            </div>
          </div>

          {editingId && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Status</label>
                <select
                  value={formData.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.value === 'active' }))}
                  className="input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={resetForm} className="btn-secondary">Cancel</button>
            <button
              onClick={handleSave}
              disabled={!formData.code || !formData.name || createSkill.isPending || updateSkill.isPending}
              className="btn-primary"
            >
              <Save className="h-4 w-4" />
              {editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {skills.length > 0 && (
        <>
        {/* Mobile cards */}
        <div className="md:hidden card overflow-hidden !p-0 divide-y divide-gray-100">
          {skills.map((skill) => (
            <div key={skill.id} className={cn("p-3 flex items-start justify-between gap-3", !skill.isActive && "opacity-50")}>
              <div className="flex-1 min-w-0">
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase" style={{ backgroundColor: skill.colorBg, color: skill.colorText }}>{skill.name}</span>
                <div className="font-mono text-[10px] text-gray-500 mt-1">{skill.code}</div>
                {skill.description && <div className="text-xs text-gray-500 mt-1">{skill.description}</div>}
              </div>
              {canManageSkills && isEditMode && (
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => handleEdit(skill)} className="p-1.5 rounded bg-gray-100 text-gray-500"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => handleDelete(skill.id)} className="p-1.5 rounded bg-gray-100 text-gray-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="hidden md:block card overflow-hidden !p-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Skill</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Description</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase text-gray-500">Status</th>
                {isEditMode && <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {skills.map((skill) => (
                <tr key={skill.id} className={cn(!skill.isActive && 'bg-gray-50 opacity-60')}>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600">{skill.code}</td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-block px-2.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                      style={{ backgroundColor: skill.colorBg, color: skill.colorText }}
                    >
                      {skill.name}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{skill.description || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn(
                      'inline-block px-2 py-0.5 rounded text-xs',
                      skill.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    )}>
                      {skill.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {isEditMode && (
                  <td className="px-4 py-3 text-right">
                    {canManageSkills && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(skill)} className="p-1 text-gray-400 hover:text-gray-600">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(skill.id)} className="p-1 text-gray-400 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}

// ============ Groups Tab ============

const AREA_LABELS: Record<string, string> = {
  pipeline: 'Pipeline & Opportunities',
  customers: 'Customers & Accounts',
  projects: 'Projects & Delivery',
  framework: 'Services Framework',
  delivery_profiles: 'Service Packages',
  rate_cards: 'Rate Cards',
  admin: 'Admin & Configuration',
  tar: 'TAR',
};

function FeatureFlagsTab() {
  const { data: flagsData, isLoading } = useFeatureFlags();
  const { data: groupsData } = useGroups();
  const updateFlag = useUpdateFeatureFlag();
  const deleteFlag = useDeleteFeatureFlag();
  const createFlag = useCreateFeatureFlag();
  const setFlagGroups = useSetFeatureFlagGroups();

  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const flags = flagsData?.featureFlags || [];
  const allGroups = groupsData?.groups || [];

  const handleToggleEnabled = (flag: FeatureFlag) => {
    updateFlag.mutate({ id: flag.id, enabled: !flag.enabled });
  };

  const handleToggleAll = (flag: FeatureFlag) => {
    updateFlag.mutate({ id: flag.id, enabledForAll: !flag.enabledForAll });
  };

  const handleDelete = (flag: FeatureFlag) => {
    if (confirm(`Delete feature flag "${flag.name}"? This cannot be undone.`)) {
      deleteFlag.mutate(flag.id);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-xtravirt-dark">Feature Flags</h2>
          <p className="text-sm text-gray-600 mt-1">
            Control which features are visible to which users. Master switch must be ON plus either "enabled for all" or group assignment.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Feature Flag
        </button>
      </div>

      {flags.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">
          No feature flags defined. Create one to start gating features.
        </div>
      ) : (
        <div className="space-y-3">
          {flags.map((flag) => (
            <div key={flag.id} className="card p-4 md:p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-xtravirt-dark">{flag.name}</h3>
                    <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-mono">
                      {flag.id}
                    </code>
                  </div>
                  {flag.description && (
                    <p className="text-sm text-gray-600 mb-3">{flag.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <button
                      onClick={() => handleToggleEnabled(flag)}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1 rounded-full font-medium transition-colors',
                        flag.enabled
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {flag.enabled ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                      {flag.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                    <button
                      onClick={() => handleToggleAll(flag)}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-1 rounded-full font-medium transition-colors',
                        flag.enabledForAll
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {flag.enabledForAll ? 'All Users' : 'Groups Only'}
                    </button>
                    {!flag.enabledForAll && (
                      <span className="text-gray-500">
                        {flag.groups.length > 0
                          ? `${flag.groups.length} group${flag.groups.length !== 1 ? 's' : ''}: ${flag.groups.map((g) => g.name).join(', ')}`
                          : 'No groups assigned'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setEditingFlag(flag)}
                    className="btn-secondary text-sm"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(flag)}
                    className="btn-secondary text-red-600 hover:bg-red-50 text-sm"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingFlag && (
        <FeatureFlagEditModal
          flag={editingFlag}
          allGroups={allGroups}
          onClose={() => setEditingFlag(null)}
          onSave={async (updates, groupIds) => {
            await updateFlag.mutateAsync({ id: editingFlag.id, ...updates });
            await setFlagGroups.mutateAsync({ id: editingFlag.id, groupIds });
            setEditingFlag(null);
          }}
        />
      )}

      {showCreateModal && (
        <FeatureFlagCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreate={async (data) => {
            await createFlag.mutateAsync(data);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
}

interface FeatureFlagEditModalProps {
  flag: FeatureFlag;
  allGroups: Array<{ id: string; code: string; name: string }>;
  onClose: () => void;
  onSave: (
    updates: { name?: string; description?: string | null; enabled?: boolean; enabledForAll?: boolean },
    groupIds: string[]
  ) => Promise<void>;
}

function FeatureFlagEditModal({ flag, allGroups, onClose, onSave }: FeatureFlagEditModalProps) {
  const [name, setName] = useState(flag.name);
  const [description, setDescription] = useState(flag.description || '');
  const [enabled, setEnabled] = useState(flag.enabled);
  const [enabledForAll, setEnabledForAll] = useState(flag.enabledForAll);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(
    new Set(flag.groups.map((g) => g.id))
  );
  const [saving, setSaving] = useState(false);

  const toggleGroup = (id: string) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(
        { name, description: description || null, enabled, enabledForAll },
        Array.from(selectedGroups)
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-lg">
        <div className="dialog-header">
          <h2 className="dialog-title">Edit Feature Flag</h2>
        </div>
        <div className="dialog-body space-y-4">
          <div>
            <label className="label">Key</label>
            <code className="block mt-1 text-sm bg-gray-50 p-2 rounded border border-gray-200 font-mono">
              {flag.id}
            </code>
          </div>
          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input w-full mt-1"
              required
            />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="input w-full mt-1"
              placeholder="What this feature does"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">Enabled (master switch)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={enabledForAll}
                onChange={(e) => setEnabledForAll(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">Enabled for all users (ignore group assignment)</span>
            </label>
          </div>

          {!enabledForAll && (
            <div>
              <label className="label">Assigned Groups</label>
              <p className="text-xs text-gray-500 mb-2">
                Users in these groups will see this feature when it's enabled.
              </p>
              <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded p-2">
                {allGroups.map((g) => (
                  <label
                    key={g.id}
                    className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedGroups.has(g.id)}
                      onChange={() => toggleGroup(g.id)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm">{g.name}</span>
                    <code className="text-xs text-gray-400 font-mono">{g.code}</code>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={!name || saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FeatureFlagCreateModalProps {
  onClose: () => void;
  onCreate: (data: {
    id: string;
    name: string;
    description?: string;
    enabled?: boolean;
    enabledForAll?: boolean;
  }) => Promise<void>;
}

function FeatureFlagCreateModal({ onClose, onCreate }: FeatureFlagCreateModalProps) {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await onCreate({ id, name, description: description || undefined });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog max-w-lg">
        <div className="dialog-header">
          <h2 className="dialog-title">Create Feature Flag</h2>
        </div>
        <div className="dialog-body space-y-4">
          <div>
            <label className="label">Key *</label>
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              className="input w-full mt-1 font-mono"
              placeholder="e.g., new_dashboard"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Lowercase letters, numbers, and underscores only. Used in code as <code>useFeature('{id || 'key'}')</code>.
            </p>
          </div>
          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input w-full mt-1"
              placeholder="e.g., New Dashboard"
              required
            />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="input w-full mt-1"
              placeholder="What this feature does"
            />
          </div>
        </div>
        <div className="dialog-footer">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={creating}>
            Cancel
          </button>
          <button onClick={handleCreate} className="btn-primary" disabled={!id || !name || creating}>
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function GroupsTab() {
  const { data: groupsData, isLoading } = useGroups();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupCode, setNewGroupCode] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const createGroup = useCreateGroup();
  const deleteGroup = useDeleteGroup();

  const groups = groupsData?.groups || [];

  useEffect(() => {
    if (groups.length > 0 && !groups.some(g => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, selectedGroupId]);

  const handleCreateGroup = async () => {
    if (!newGroupCode.trim() || !newGroupName.trim()) return;
    try {
      const res = await createGroup.mutateAsync({ code: newGroupCode.trim(), name: newGroupName.trim() });
      setShowCreateGroup(false);
      setNewGroupCode('');
      setNewGroupName('');
      setSelectedGroupId(res.group.id);
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    }
  };

  const handleDeleteGroup = async (id: string, name: string) => {
    if (!confirm(`Delete group "${name}"? Members will be removed from this group.`)) return;
    try {
      await deleteGroup.mutateAsync(id);
      if (selectedGroupId === id) setSelectedGroupId(null);
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium text-gray-900">Groups & Permissions</h3>
        <p className="text-xs text-gray-500 mt-1">
          Users inherit permissions from the groups they belong to. Admin users automatically have all permissions.
        </p>
      </div>

      <div className="flex gap-4 h-[calc(100vh-300px)] min-h-[400px]">
        {/* Left: Group list */}
        <div className="w-64 flex-shrink-0 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900 text-sm">Groups ({groups.length})</h4>
              <button
                onClick={() => setShowCreateGroup(true)}
                className="p-1 text-gray-500 hover:text-xtravirt-cyan hover:bg-gray-100 rounded"
                title="Add Group"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {groups.map((g) => (
              <div
                key={g.id}
                className={cn(
                  'px-3 py-2.5 cursor-pointer border-b border-gray-100 transition-colors group',
                  selectedGroupId === g.id ? 'bg-blue-50' : 'hover:bg-gray-50'
                )}
                onClick={() => setSelectedGroupId(g.id)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 truncate">{g.name}</span>
                  {g.isSystem && (
                    <span className="text-[9px] uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 ml-1">
                      System
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {g.memberCount} member{g.memberCount === 1 ? '' : 's'} · {g.permissionCount} permission{g.permissionCount === 1 ? '' : 's'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Group detail with members + permissions */}
        <div className="flex-1 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
          {selectedGroupId ? (
            <GroupDetailPanel groupId={selectedGroupId} onDelete={handleDeleteGroup} />
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              Select a group to view details
            </div>
          )}
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="dialog-overlay">
          <div className="dialog max-w-md">
            <div className="dialog-header">
              <h2 className="dialog-title">Create Group</h2>
            </div>
            <div className="dialog-body space-y-4">
              <div>
                <label className="label">Code *</label>
                <input
                  type="text"
                  value={newGroupCode}
                  onChange={(e) => setNewGroupCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                  className="input mt-1 w-full"
                  placeholder="e.g., xv_delivery_team"
                  autoFocus
                />
              </div>
              <div>
                <label className="label">Name *</label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className="input mt-1 w-full"
                  placeholder="e.g., XV Delivery Team"
                />
              </div>
            </div>
            <div className="dialog-footer">
              <button onClick={() => setShowCreateGroup(false)} className="btn-secondary">Cancel</button>
              <button
                onClick={handleCreateGroup}
                className="btn-primary"
                disabled={!newGroupCode.trim() || !newGroupName.trim() || createGroup.isPending}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GroupDetailPanel({ groupId, onDelete }: { groupId: string; onDelete: (id: string, name: string) => void }) {
  const { data, isLoading } = useGroup(groupId);
  const { data: catalogueData } = usePermissionCatalogue();
  const { data: usersData } = useUsers();
  const setPerms = useSetGroupPermissions();
  const addMember = useAddGroupMember();
  const removeMember = useRemoveGroupMember();
  const [showAddMember, setShowAddMember] = useState(false);

  // Local state for batched permission edits
  const [localPerms, setLocalPerms] = useState<Set<string> | null>(null);
  const effectivePerms = localPerms ?? new Set(data?.permissions || []);
  const hasPermChanges = localPerms !== null;

  // Reset local state when group changes
  useEffect(() => {
    setLocalPerms(null);
  }, [groupId]);

  if (isLoading || !data) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const { group, members } = data;
  const catalogue = catalogueData?.permissions || {};
  const allUsers = usersData?.users || [];
  const memberIds = new Set(members.map(m => m.id));
  const availableUsers = allUsers.filter(u => u.status === 'active' && !memberIds.has(u.id));

  const togglePerm = (code: string) => {
    const next = new Set(effectivePerms);
    if (next.has(code)) next.delete(code); else next.add(code);
    setLocalPerms(next);
  };

  const toggleArea = (area: string) => {
    const areaPerms = (catalogue[area] || []).map(p => p.code);
    const allChecked = areaPerms.every(code => effectivePerms.has(code));
    const next = new Set(effectivePerms);
    for (const code of areaPerms) {
      if (allChecked) next.delete(code); else next.add(code);
    }
    setLocalPerms(next);
  };

  const handleSavePerms = async () => {
    if (!localPerms) return;
    try {
      await setPerms.mutateAsync({ groupId, permissions: Array.from(localPerms) });
      setLocalPerms(null);
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    }
  };

  const handleAddMember = async (userId: string) => {
    try {
      await addMember.mutateAsync({ groupId, userId });
      setShowAddMember(false);
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">{group.name}</h3>
            <p className="text-xs text-gray-500 font-mono mt-0.5">{group.code}</p>
          </div>
          <div className="flex items-center gap-2">
            {group.isSystem && (
              <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">
                System group
              </span>
            )}
            {!group.isSystem && (
              <button
                onClick={() => onDelete(group.id, group.name)}
                className="btn-secondary btn-sm text-xs text-red-600 hover:bg-red-50"
              >
                Delete group
              </button>
            )}
          </div>
        </div>
        {group.description && (
          <p className="text-sm text-gray-600 mt-1">{group.description}</p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Members section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">Members ({members.length})</h4>
            <button
              onClick={() => setShowAddMember(!showAddMember)}
              className="flex items-center gap-1 text-xs text-xtravirt-cyan hover:text-xtravirt-navy"
            >
              <Plus className="h-3 w-3" /> Add member
            </button>
          </div>
          {showAddMember && availableUsers.length > 0 && (
            <div className="mb-3 rounded border border-gray-200 bg-gray-50 p-2 max-h-32 overflow-y-auto">
              {availableUsers.map(u => (
                <button
                  key={u.id}
                  onClick={() => handleAddMember(u.id)}
                  className="w-full text-left px-2 py-1.5 text-xs text-gray-700 hover:bg-blue-50 rounded flex items-center justify-between"
                >
                  <span>{u.name} ({u.email})</span>
                  <Plus className="h-3 w-3 text-gray-400" />
                </button>
              ))}
            </div>
          )}
          {showAddMember && availableUsers.length === 0 && (
            <p className="text-xs text-gray-500 mb-3">All users are already in this group.</p>
          )}
          {members.length === 0 ? (
            <p className="text-xs text-gray-500">No members</p>
          ) : (
            <div className="space-y-1">
              {members.map(m => (
                <div key={m.id} className="flex items-center justify-between rounded bg-gray-50 px-3 py-1.5 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-xtravirt-cyan text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                      {m.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-gray-900">{m.name}</span>
                    <span className="text-gray-500 text-xs">{m.email}</span>
                  </div>
                  <button
                    onClick={() => removeMember.mutate({ groupId, userId: m.id })}
                    className="text-gray-400 hover:text-red-500 p-1"
                    title="Remove from group"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Permissions section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">
              Permissions ({effectivePerms.size})
            </h4>
            {hasPermChanges && (
              <div className="flex items-center gap-2">
                <button onClick={() => setLocalPerms(null)} className="btn-secondary btn-sm text-xs">
                  Discard
                </button>
                <button onClick={handleSavePerms} disabled={setPerms.isPending} className="btn-primary btn-sm text-xs">
                  {setPerms.isPending ? 'Saving…' : 'Save permissions'}
                </button>
              </div>
            )}
          </div>
          <div className="space-y-3">
            {Object.entries(catalogue).map(([area, perms]) => {
              const areaCheckedCount = perms.filter(p => effectivePerms.has(p.code)).length;
              return (
                <div key={area} className="rounded border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => toggleArea(area)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {AREA_LABELS[area] || area}
                    </span>
                    <span className="text-[10px] text-gray-500">
                      {areaCheckedCount}/{perms.length}
                    </span>
                  </button>
                  <div className="px-3 py-2 space-y-1.5">
                    {perms.map(p => (
                      <label key={p.code} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={effectivePerms.has(p.code)}
                          onChange={() => togglePerm(p.code)}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-xtravirt-cyan focus:ring-xtravirt-cyan"
                        />
                        <span className="text-xs text-gray-700">{p.description}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ Settings Tab ============

function SettingsTab() {
  const { data, isLoading } = useSystemSettings();
  const updateSettings = useUpdateSystemSettings();

  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const settings = data?.settings || [];
  const groupedSettings = groupSettingsByCategory(settings);

  const handleChange = useCallback((key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  }, []);

  const getValue = useCallback((setting: SystemSetting) => {
    return editedValues[setting.key] !== undefined ? editedValues[setting.key] : setting.value;
  }, [editedValues]);

  const handleSave = useCallback(async () => {
    const changedSettings = Object.entries(editedValues).map(([key, value]) => ({ key, value }));
    if (changedSettings.length === 0) return;

    try {
      await updateSettings.mutateAsync({ settings: changedSettings });
      setEditedValues({});
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }, [editedValues, updateSettings]);

  const handleReset = useCallback(() => {
    setEditedValues({});
    setHasChanges(false);
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const categoryLabels: Record<string, string> = {
    pricing: 'Pricing & Calculation Defaults',
    general: 'General Settings',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            System-wide configuration settings
          </p>
          <p className="text-xs text-gray-500 mt-1">
            These defaults are used when creating new projects and rate cards
          </p>
        </div>
        {hasChanges && (
          <div className="flex items-center gap-3">
            <button onClick={handleReset} className="btn-secondary">
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={updateSettings.isPending}
              className="btn-primary"
            >
              <Save className="h-4 w-4" />
              {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Settings by Category */}
      {Object.entries(groupedSettings).map(([category, categorySettings]) => (
        <div key={category} className="card overflow-hidden !p-0">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">
              {categoryLabels[category] || category}
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {categorySettings.map((setting) => (
              <div key={setting.key} className="px-4 py-4 flex items-center justify-between">
                <div className="flex-1">
                  <label className="font-medium text-gray-900">{setting.label}</label>
                  {setting.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{setting.description}</p>
                  )}
                </div>
                <div className="ml-4 w-48">
                  {setting.type === 'number' ? (
                    <input
                      type="number"
                      value={getValue(setting)}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                      className="input text-right"
                    />
                  ) : setting.type === 'boolean' ? (
                    <select
                      value={getValue(setting)}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                      className="input"
                    >
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={getValue(setting)}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                      className="input"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ Master Data Tab ============

type WorkstreamPhase = 'assess' | 'design' | 'deploy' | 'migrate' | 'operate' | 'optimise';
type WorkstreamPillar = 'infrastructure' | 'application' | 'security';

function MasterDataTab() {
  const { data: workstreamsData, isLoading } = useWorkstreams({
    includeCapabilities: true,
    includeActivities: true,
    includeInactive: false,
  });
  const { data: rolesData } = useResourceRoles();

  // Edit mode + change set state
  const { changeSetId, isEditMode, enterEditMode, exitEditMode } = useEditMode();
  const createChangeSet = useCreateChangeSet();
  const discardChangeSet = useDiscardChangeSet();
  const submitChangeSet = useSubmitChangeSet();
  const { data: changeSetDetail } = useFrameworkChangeSet(changeSetId);
  const { data: submittedSetsData } = useFrameworkChangeSets('submitted');
  const submittedCount = submittedSetsData?.changeSets?.length || 0;
  const pendingItems: FrameworkChangeItem[] = changeSetDetail?.items || [];
  // Per-entity-type pending maps for visual badges
  const pendingActivityMap = buildPendingMap(pendingItems, 'activity');
  const pendingCapabilityMap = buildPendingMap(pendingItems, 'capability');
  const pendingWorkstreamMap = buildPendingMap(pendingItems, 'workstream');
  const pendingGuardrailMap = buildPendingMap(pendingItems, 'guardrail');
  const { recordChange, undoChange } = useRecordChange(changeSetId, pendingItems);
  // Pending CREATE items for an entity type — used to inject not-yet-applied entities
  // into the live view so the user can see and edit them
  const pendingCreatedActivities = pendingItems.filter(it => it.entityType === 'activity' && it.changeType === 'create');
  const pendingCreatedCapabilities = pendingItems.filter(it => it.entityType === 'capability' && it.changeType === 'create');
  const pendingCreatedWorkstreams = pendingItems.filter(it => it.entityType === 'workstream' && it.changeType === 'create');
  const pendingCreatedGuardrails = pendingItems.filter(it => it.entityType === 'guardrail' && it.changeType === 'create');

  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showPendingReview, setShowPendingReview] = useState(false);
  const [showApprovalsPanel, setShowApprovalsPanel] = useState(false);
  const [showVersionsPanel, setShowVersionsPanel] = useState(false);

  // No live mutation hooks here — every CUD goes through `recordChange` so the user
  // sees a pending change item rather than a live mutation.

  const handleEnterEditMode = async () => {
    try {
      const res = await createChangeSet.mutateAsync();
      enterEditMode(res.changeSet.id);
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed to enter edit mode: ${err.message || 'unknown error'}`);
    }
  };

  const handleDiscard = async () => {
    if (!changeSetId) return;
    if (!confirm(`Discard ${pendingItems.length} pending change(s)? This cannot be undone.`)) return;
    try {
      await discardChangeSet.mutateAsync(changeSetId);
    } catch (e) {
      // If the change set is already gone (NOT_FOUND), treat as success
      const err = e as { message?: string };
      if (!err.message?.includes('NOT_FOUND') && !err.message?.includes('not found')) {
        alert(`Failed to discard: ${err.message || 'unknown error'}`);
        return;
      }
    }
    // Always exit edit mode — the change set is gone (or was never there)
    exitEditMode();
  };

  const [selectedWorkstreamId, setSelectedWorkstreamId] = useState<string | null>(null);
  const [selectedCapabilityId, setSelectedCapabilityId] = useState<string | null>(null);
  const [wsSearch, setWsSearch] = useState('');
  const [capSearch, setCapSearch] = useState('');

  // Modal states
  const [editingWorkstream, setEditingWorkstream] = useState<VcfWorkstream | null>(null);
  const [showAddWorkstream, setShowAddWorkstream] = useState(false);
  const [editingCapability, setEditingCapability] = useState<VcfCapability | null>(null);
  const [showAddCapability, setShowAddCapability] = useState(false);
  const [editingActivity, setEditingActivity] = useState<VcfActivity | null>(null);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [editingGuardrail, setEditingGuardrail] = useState<VcfGuardrail | null>(null);
  const [showAddGuardrail, setShowAddGuardrail] = useState(false);

  const liveWorkstreams = workstreamsData?.workstreams || [];
  const roles = rolesData?.roles || [];

  /**
   * Merge live workstreams with pending CREATE items so the user sees their not-yet-applied
   * additions inline. Pending UPDATE items are merged on top of the live entity (so a UPDATE
   * to "name" appears in the list immediately). Pending DELETE items are NOT removed from
   * the list — instead they get a strikethrough badge and an Undo button (so the user can
   * back out before submitting).
   */
  const workstreams = useMemoMergedWorkstreams(
    liveWorkstreams,
    pendingCreatedWorkstreams,
    pendingCreatedCapabilities,
    pendingCreatedActivities,
    pendingCreatedGuardrails,
    pendingWorkstreamMap,
    pendingCapabilityMap,
    pendingActivityMap,
    pendingGuardrailMap,
  );

  const selectedWorkstream = workstreams.find((ws) => ws.id === selectedWorkstreamId);
  const selectedCapability = selectedWorkstream?.capabilities?.find((cap) => cap.id === selectedCapabilityId);

  // ========== Mutation handlers via change items ==========

  /** Build the afterJson for a CREATE — only the columns that exist on the table */
  const handleSaveActivity = async (
    data: { name: string; role: string; baseDays: number; skill?: string; skillId?: string; description?: string; activityPhase?: string; isMilestone?: boolean; customerDays?: number; customerRole?: string }
  ) => {
    if (!selectedCapability) return;
    if (!isEditMode) return;
    try {
      if (editingActivity) {
        // UPDATE — only the editable fields go into afterJson
        const after = {
          name: data.name,
          role: data.role,
          baseDays: data.baseDays,
          skill: data.skill ?? null,
          skillId: data.skillId ?? null,
          description: data.description ?? null,
          activityPhase: data.activityPhase ?? null,
          isMilestone: data.isMilestone ? 1 : 0,
          customerDays: data.customerDays ?? 0,
          customerRole: data.customerRole ?? null,
        };
        await recordChange({
          entityType: 'activity',
          entityId: editingActivity.id,
          changeType: 'update',
          beforeJson: JSON.stringify(activityToColumns(editingActivity)),
          afterJson: JSON.stringify(after),
        });
      } else {
        // CREATE — full row
        const newId = generateEntityId('act');
        const after = {
          id: newId,
          capabilityId: selectedCapability.id,
          name: data.name,
          role: data.role,
          skill: data.skill ?? null,
          skillId: data.skillId ?? null,
          baseDays: data.baseDays,
          description: data.description ?? null,
          activityPhase: data.activityPhase ?? null,
          isMilestone: data.isMilestone ? 1 : 0,
          customerDays: data.customerDays ?? 0,
          customerRole: data.customerRole ?? null,
          sortOrder: 0,
          isActive: 1,
        };
        await recordChange({
          entityType: 'activity',
          entityId: newId,
          changeType: 'create',
          afterJson: JSON.stringify(after),
        });
      }
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    } finally {
      setEditingActivity(null);
      setShowAddActivity(false);
    }
  };

  const handleSaveCapability = async (
    data: { code: string; name: string; description?: string; cls?: 'CORE' | 'RECOMMENDED' | 'OPTIONAL'; defaultIncluded?: boolean; isActive?: boolean }
  ) => {
    if (!selectedWorkstream) return;
    if (!isEditMode) return;
    try {
      if (editingCapability) {
        const after = {
          code: data.code,
          name: data.name,
          description: data.description ?? null,
          cls: data.cls ?? null,
          defaultIncluded: data.defaultIncluded ? 1 : 0,
        };
        await recordChange({
          entityType: 'capability',
          entityId: editingCapability.id,
          changeType: 'update',
          beforeJson: JSON.stringify(capabilityToColumns(editingCapability)),
          afterJson: JSON.stringify(after),
        });
      } else {
        const newId = generateEntityId('cap');
        const after = {
          id: newId,
          workstreamId: selectedWorkstream.id,
          code: data.code,
          name: data.name,
          description: data.description ?? null,
          cls: data.cls ?? null,
          defaultIncluded: data.defaultIncluded ? 1 : 0,
          sortOrder: 0,
          isActive: 1,
        };
        await recordChange({
          entityType: 'capability',
          entityId: newId,
          changeType: 'create',
          afterJson: JSON.stringify(after),
        });
      }
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    } finally {
      setEditingCapability(null);
      setShowAddCapability(false);
    }
  };

  const handleSaveWorkstream = async (
    data: { code: string; name: string; description?: string; phase?: WorkstreamPhase; pillar?: WorkstreamPillar; category?: string; isActive?: boolean }
  ) => {
    if (!isEditMode) return;
    try {
      if (editingWorkstream) {
        const after = {
          code: data.code,
          name: data.name,
          description: data.description ?? null,
          phase: data.phase ?? null,
          pillar: data.pillar ?? null,
          category: data.category ?? null,
        };
        await recordChange({
          entityType: 'workstream',
          entityId: editingWorkstream.id,
          changeType: 'update',
          beforeJson: JSON.stringify(workstreamToColumns(editingWorkstream)),
          afterJson: JSON.stringify(after),
        });
      } else {
        const newId = generateEntityId('ws');
        const after = {
          id: newId,
          code: data.code,
          name: data.name,
          description: data.description ?? null,
          phase: data.phase ?? null,
          pillar: data.pillar ?? null,
          category: data.category ?? null,
          sortOrder: 0,
          isActive: 1,
        };
        await recordChange({
          entityType: 'workstream',
          entityId: newId,
          changeType: 'create',
          afterJson: JSON.stringify(after),
        });
      }
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    } finally {
      setEditingWorkstream(null);
      setShowAddWorkstream(false);
    }
  };

  const handleSaveGuardrail = async (
    data: { name: string; code: string; baseline?: string; multiplier?: string; baselineValue?: number; multiplierFactor?: number; multiplierStep?: number; multiplierLabel?: string; optionFactors?: Record<string, number>; inputType?: 'number' | 'select' | 'boolean'; options?: string[]; defaultValue?: string; minValue?: number; maxValue?: number; linkedActivityIds?: string[] }
  ) => {
    if (!selectedCapability) return;
    if (!isEditMode) return;
    try {
      if (editingGuardrail) {
        const after = {
          name: data.name,
          code: data.code,
          baseline: data.baseline ?? null,
          multiplier: data.multiplier ?? null,
          baselineValue: data.baselineValue ?? null,
          multiplierFactor: data.multiplierFactor ?? null,
          multiplierStep: data.multiplierStep ?? null,
          multiplierLabel: data.multiplierLabel ?? null,
          optionFactors: data.optionFactors ? JSON.stringify(data.optionFactors) : null,
          inputType: data.inputType ?? 'number',
          options: data.options ? JSON.stringify(data.options) : null,
          defaultValue: data.defaultValue ?? null,
          minValue: data.minValue ?? null,
          maxValue: data.maxValue ?? null,
        };
        await recordChange({
          entityType: 'guardrail',
          entityId: editingGuardrail.id,
          changeType: 'update',
          beforeJson: JSON.stringify(guardrailToColumns(editingGuardrail)),
          afterJson: JSON.stringify(after),
        });
      } else {
        const newId = generateEntityId('grd');
        const after = {
          id: newId,
          capabilityId: selectedCapability.id,
          name: data.name,
          code: data.code,
          baseline: data.baseline ?? null,
          multiplier: data.multiplier ?? null,
          baselineValue: data.baselineValue ?? null,
          multiplierFactor: data.multiplierFactor ?? null,
          multiplierStep: data.multiplierStep ?? null,
          multiplierLabel: data.multiplierLabel ?? null,
          optionFactors: data.optionFactors ? JSON.stringify(data.optionFactors) : null,
          inputType: data.inputType ?? 'number',
          options: data.options ? JSON.stringify(data.options) : null,
          defaultValue: data.defaultValue ?? null,
          minValue: data.minValue ?? null,
          maxValue: data.maxValue ?? null,
          sortOrder: 0,
        };
        await recordChange({
          entityType: 'guardrail',
          entityId: newId,
          changeType: 'create',
          afterJson: JSON.stringify(after),
        });
      }
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    } finally {
      setEditingGuardrail(null);
      setShowAddGuardrail(false);
    }
  };

  const handleDeleteEntity = async (
    entityType: 'workstream' | 'capability' | 'guardrail',
    id: string,
    name: string,
    before: VcfWorkstream | VcfCapability | VcfGuardrail
  ) => {
    if (!isEditMode) return;
    if (!confirm(
      `Delete "${name}"?\n\n` +
      `This deletion will be applied when the change set is approved. ` +
      `${entityType === 'workstream' ? 'All capabilities, activities and guardrails under this workstream will also be removed.' : ''}` +
      `${entityType === 'capability' ? 'All activities and guardrails under this capability will also be removed.' : ''}` +
      `\n\nLocked projects (submitted, approved, archived) are unaffected — they use snapshots.`
    )) return;
    try {
      await recordChange({
        entityType,
        entityId: id,
        changeType: 'delete',
        beforeJson: JSON.stringify(before),
      });
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    }
  };

  // Validate capability selection — clear if it no longer exists in the selected workstream
  React.useEffect(() => {
    if (selectedWorkstreamId && selectedWorkstream?.capabilities?.length && selectedCapabilityId) {
      const capExists = selectedWorkstream.capabilities.some((c) => c.id === selectedCapabilityId);
      if (!capExists) {
        setSelectedCapabilityId(null);
      }
    }
  }, [selectedWorkstream, selectedWorkstreamId, selectedCapabilityId]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Edit Mode Banner — shown only when actively editing */}
      {isEditMode && changeSetDetail && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 overflow-hidden">
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3">
            <div className="flex items-center gap-2 min-w-0">
              <Pencil className="h-4 w-4 text-amber-700 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-amber-900 truncate">
                  Editing Services Framework — {pendingItems.length} pending change{pendingItems.length === 1 ? '' : 's'}
                </div>
                <div className="text-xs text-amber-800">
                  Changes require approval before going live
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {pendingItems.length > 0 && (
                <button
                  onClick={() => setShowPendingReview(!showPendingReview)}
                  className={cn("btn-secondary btn-sm text-xs flex items-center gap-1", showPendingReview && "bg-amber-100 border-amber-300")}
                >
                  {showPendingReview ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  Review
                </button>
              )}
              <button
                onClick={handleDiscard}
                className="btn-secondary btn-sm text-xs flex items-center gap-1"
                disabled={discardChangeSet.isPending}
              >
                <Undo2 className="h-3 w-3" /> Discard
              </button>
              <button
                onClick={() => setShowSubmitModal(true)}
                className="btn-primary btn-sm text-xs flex items-center gap-1"
                disabled={pendingItems.length === 0}
                title={pendingItems.length === 0 ? 'No pending changes to submit' : 'Submit for approval'}
              >
                <Send className="h-3 w-3" /> Submit for review
              </button>
            </div>
          </div>
          {/* Expandable review section */}
          {showPendingReview && pendingItems.length > 0 && (
            <div className="border-t border-amber-200 bg-amber-50/50 px-4 py-3 space-y-2 max-h-[300px] overflow-y-auto">
              <div className="text-xs font-medium text-amber-900 mb-2">Pending changes:</div>
              {pendingItems.map((item) => (
                <ChangeItemRow key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Live View Header with Edit Framework button — above multipliers */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            {workstreams.length} workstreams with{' '}
            {workstreams.reduce((sum, ws) => sum + (ws.capabilities?.length || 0), 0)} capabilities
          </p>
          <p className="text-xs text-gray-500 mt-1 hidden md:block">
            {isEditMode
              ? 'In edit mode — changes go into a pending change set and only apply after approval'
              : 'Live view — click Edit Services Framework to propose changes that go through approval'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowApprovalsPanel(true)} className="btn-secondary btn-sm flex items-center gap-1.5 text-xs">
            <Check className="h-3.5 w-3.5" /> Approvals
            {submittedCount > 0 && (
              <span className="inline-flex items-center justify-center h-4 min-w-[1rem] px-1 text-[10px] font-bold rounded-full bg-amber-500 text-white">
                {submittedCount}
              </span>
            )}
          </button>
          <button onClick={() => setShowVersionsPanel(true)} className="btn-secondary btn-sm flex items-center gap-1.5 text-xs">
            <History className="h-3.5 w-3.5" /> Versions
          </button>
          {!isEditMode && (
            <button onClick={handleEnterEditMode} disabled={createChangeSet.isPending} className="btn-primary btn-sm flex items-center gap-1.5 text-xs">
              <Pencil className="h-3.5 w-3.5" /> <span className="md:hidden">Edit</span><span className="hidden md:inline">Edit Services Framework</span>
            </button>
          )}
        </div>
      </div>

      {/* General Multipliers — collapsible panel */}
      <CollapsibleMultipliers />



      {/* Breadcrumb navigation */}
      <div className="flex items-center gap-1.5 text-sm py-1 overflow-x-auto whitespace-nowrap">
        <button
          onClick={() => { setSelectedWorkstreamId(null); setSelectedCapabilityId(null); }}
          className={cn(
            !selectedWorkstreamId ? 'font-semibold text-gray-900 cursor-default' : 'text-xtravirt-cyan hover:underline'
          )}
        >
          Workstreams
        </button>
        {selectedWorkstream && (
          <>
            <ChevronRight className="h-3 w-3 text-gray-300" />
            <button
              onClick={() => setSelectedCapabilityId(null)}
              className={cn(
                !selectedCapabilityId ? 'font-semibold text-gray-900 cursor-default' : 'text-xtravirt-cyan hover:underline'
              )}
            >
              {selectedWorkstream.name}
            </button>
          </>
        )}
        {selectedCapability && (
          <>
            <ChevronRight className="h-3 w-3 text-gray-300" />
            <span className="font-semibold text-gray-900">{selectedCapability.name}</span>
            {selectedCapability.cls && (
              <span className={cn(
                'text-[8px] font-bold px-1.5 py-0.5 rounded',
                selectedCapability.cls === 'CORE' && 'bg-blue-100 text-blue-700',
                selectedCapability.cls === 'RECOMMENDED' && 'bg-green-100 text-green-700',
                selectedCapability.cls === 'OPTIONAL' && 'bg-gray-100 text-gray-600',
              )}>
                {selectedCapability.cls}
              </span>
            )}
          </>
        )}
      </div>

      {/* Step 1: Workstream list (full width) */}
      {!selectedWorkstreamId && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h3 className="font-semibold text-gray-900">Workstreams</h3>
              <p className="text-xs text-gray-500 mt-1">{workstreams.length} workstreams · {workstreams.reduce((s, ws) => s + (ws.capabilities?.length || 0), 0)} capabilities</p>
            </div>
            {isEditMode && (
              <button onClick={() => setShowAddWorkstream(true)} className="btn-secondary btn-sm text-xs flex items-center gap-1">
                <Plus className="h-3 w-3" /> Add Workstream
              </button>
            )}
          </div>
          <div className="p-3 border-b border-gray-100">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={wsSearch}
                onChange={(e) => setWsSearch(e.target.value)}
                placeholder="Search workstreams..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-xtravirt-cyan"
              />
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {workstreams
              .filter(ws => !wsSearch || ws.name.toLowerCase().includes(wsSearch.toLowerCase()) || ws.code.toLowerCase().includes(wsSearch.toLowerCase()))
              .map(ws => {
                const totalDays = (ws.capabilities || []).reduce((sum, c) => sum + (c.activities || []).reduce((s, a) => s + a.baseDays, 0), 0) / 100;
                const pendingItem = pendingWorkstreamMap.get(ws.id);
                return (
                  <button
                    key={ws.id}
                    onClick={() => { setSelectedWorkstreamId(ws.id); setWsSearch(''); }}
                    className={cn(
                      'w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors',
                      pendingItem?.changeType === 'create' && 'bg-green-50',
                      pendingItem?.changeType === 'update' && 'bg-amber-50',
                      pendingItem?.changeType === 'delete' && 'bg-red-50',
                    )}
                  >
                    <span className="font-mono text-xs text-xtravirt-cyan font-semibold w-16 flex-shrink-0">{ws.code}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">{ws.name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {ws.phase && <span className="capitalize">{ws.phase}</span>}
                        {' · '}{ws.capabilities?.length || 0} capabilities · {totalDays.toFixed(0)}d
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Step 2: Capability list (full width) */}
      {selectedWorkstreamId && !selectedCapabilityId && selectedWorkstream && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h3 className="font-semibold text-gray-900">{selectedWorkstream.name}</h3>
              <p className="text-xs text-gray-500 mt-1">{selectedWorkstream.code} · {selectedWorkstream.phase} · {selectedWorkstream.capabilities?.length || 0} capabilities</p>
            </div>
            {isEditMode && (
              <button onClick={() => setShowAddCapability(true)} className="btn-secondary btn-sm text-xs flex items-center gap-1">
                <Plus className="h-3 w-3" /> Add Capability
              </button>
            )}
          </div>
          <div className="p-3 border-b border-gray-100">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={capSearch}
                onChange={(e) => setCapSearch(e.target.value)}
                placeholder="Search capabilities..."
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-xtravirt-cyan"
              />
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {(selectedWorkstream.capabilities || [])
              .filter(cap => !capSearch || cap.name.toLowerCase().includes(capSearch.toLowerCase()) || cap.code.toLowerCase().includes(capSearch.toLowerCase()))
              .map(cap => {
                const totalDays = (cap.activities || []).reduce((s, a) => s + a.baseDays, 0) / 100;
                const pendingItem = pendingCapabilityMap.get(cap.id);
                return (
                  <button
                    key={cap.id}
                    onClick={() => { setSelectedCapabilityId(cap.id); setCapSearch(''); }}
                    className={cn(
                      'w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-gray-50 transition-colors',
                      pendingItem?.changeType === 'create' && 'bg-green-50',
                      pendingItem?.changeType === 'update' && 'bg-amber-50',
                      pendingItem?.changeType === 'delete' && 'bg-red-50',
                    )}
                  >
                    <span className="font-mono text-xs text-gray-500 w-24 flex-shrink-0">{cap.code}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        {cap.name}
                        {cap.cls && (
                          <span className={cn(
                            'text-[8px] font-bold px-1.5 py-0.5 rounded flex-shrink-0',
                            cap.cls === 'CORE' && 'bg-blue-100 text-blue-700',
                            cap.cls === 'RECOMMENDED' && 'bg-green-100 text-green-700',
                            cap.cls === 'OPTIONAL' && 'bg-gray-100 text-gray-600',
                          )}>
                            {cap.cls}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {cap.activities?.length || 0} activities · {totalDays.toFixed(1)}d · {cap.guardrails?.length || 0} guardrails
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* Step 3: Full-width activity + allocation content */}
      {selectedWorkstreamId && selectedCapabilityId && (
        <ActivityAllocationColumn
          selectedCapability={selectedCapability ?? null}
          isEditMode={isEditMode}
          changeSetId={changeSetId}
          pendingActivityMap={pendingActivityMap}
          pendingGuardrailMap={pendingGuardrailMap}
          recordChange={recordChange}
          undoChange={undoChange}
          setEditingActivity={setEditingActivity}
          setShowAddActivity={setShowAddActivity}
          setEditingGuardrail={setEditingGuardrail}
          setShowAddGuardrail={setShowAddGuardrail}
          handleDeleteEntity={handleDeleteEntity as (entityType: string, entityId: string, name: string, entity: unknown) => void}
          roles={roles}
        />
      )}

      {/* Workstream Modal */}
      {(showAddWorkstream || editingWorkstream) && (
        <WorkstreamModal
          workstream={editingWorkstream}
          onSave={handleSaveWorkstream}
          onDelete={editingWorkstream ? () => {
            handleDeleteEntity('workstream', editingWorkstream.id, editingWorkstream.name, editingWorkstream);
            setEditingWorkstream(null);
            if (selectedWorkstreamId === editingWorkstream.id) {
              setSelectedWorkstreamId(null);
            }
          } : undefined}
          onClose={() => { setEditingWorkstream(null); setShowAddWorkstream(false); }}
        />
      )}

      {/* Capability Modal */}
      {(showAddCapability || editingCapability) && selectedWorkstream && (
        <CapabilityModal
          capability={editingCapability}
          onSave={handleSaveCapability}
          onDelete={editingCapability ? () => {
            handleDeleteEntity('capability', editingCapability.id, editingCapability.name, editingCapability);
            setEditingCapability(null);
            if (selectedCapabilityId === editingCapability.id) {
              setSelectedCapabilityId(null);
            }
          } : undefined}
          onClose={() => { setEditingCapability(null); setShowAddCapability(false); }}
        />
      )}

      {/* Activity Modal */}
      {(showAddActivity || editingActivity) && selectedCapability && (
        <MasterActivityModal
          activity={editingActivity}
          roles={roles}
          capabilityId={selectedCapability.id}
          siblingActivities={(selectedCapability.activities || []).map(a => ({ id: a.id, name: a.name }))}
          onSave={handleSaveActivity}
          onClose={() => { setEditingActivity(null); setShowAddActivity(false); }}
        />
      )}

      {/* Guardrail Modal */}
      {(showAddGuardrail || editingGuardrail) && selectedCapability && (
        <MasterGuardrailModal
          guardrail={editingGuardrail}
          capabilityActivities={(selectedCapability.activities || []).map(a => ({ id: a.id, name: a.name }))}
          linkedActivityIds={[]}
          existingDefinitionIds={[]}
          onSave={handleSaveGuardrail}
          onClose={() => { setEditingGuardrail(null); setShowAddGuardrail(false); }}
        />
      )}

      {/* Submit Change Set Modal */}
      {showSubmitModal && changeSetId && (
        <SubmitChangeSetModal
          changeSetId={changeSetId}
          itemCount={pendingItems.length}
          onClose={() => setShowSubmitModal(false)}
          onSubmitted={() => {
            setShowSubmitModal(false);
            exitEditMode();
          }}
          submitChangeSet={submitChangeSet}
        />
      )}

      <ApprovalsSidePanel isOpen={showApprovalsPanel} onClose={() => setShowApprovalsPanel(false)} />
      <VersionsSidePanel isOpen={showVersionsPanel} onClose={() => setShowVersionsPanel(false)} />
    </div>
  );
}

// ============ Delivery Profiles Tab ============

const NUM_WEEKS = 12;
const DAYS_PER_WEEK = 5;

// Sub-tabs for profile editor
type ProfileSubTabId = 'details' | 'delivery-plan' | 'versions';

const PROFILE_SUB_TABS: Array<{ id: ProfileSubTabId; label: string; icon: typeof FileText }> = [
  { id: 'details', label: 'Package Details', icon: FileText },
  { id: 'delivery-plan', label: 'Delivery Plan', icon: Layers },
  { id: 'versions', label: 'Version History', icon: History },
];

// Local state for delivery plan editing
interface DeliveryPlanState {
  capabilities: Map<string, { isIncluded: boolean; startWeek: number }>;
  allocations: Map<string, Map<number, number>>; // activityId -> week -> DECIMAL days (not centdays; the backend stores centdays but we normalise here to match the Generate output and the grid inputs which have step=0.5, max=5)
  activityBaseDays: Map<string, number>; // activityId -> centdays
}

// Service Packages tab — temporarily disabled, to be re-enabled later
export function DeliveryProfilesTab() {
  // Data fetching
  const { data: profilesData, isLoading: profilesLoading } = useDeliveryProfiles();
  const { data: workstreamsData, isLoading: workstreamsLoading } = useWorkstreams({
    includeCapabilities: true,
    includeActivities: true,
    includeInactive: false,
  });
  const { data: skillsData } = useSkills();
  const { data: rolesDataLocal } = useResourceRoles();
  const skillsList = skillsData?.skills || [];
  const rolesList = rolesDataLocal?.roles || [];

  // Profile selection
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const { data: selectedProfileData, isLoading: profileLoading } = useDeliveryProfile(selectedProfileId);
  const { data: deliveryPlanData, isLoading: deliveryPlanLoading } = useProfileDeliveryPlan(selectedProfileId);
  const { data: versionsData } = useProfileVersions(selectedProfileId);

  // Mutations
  const createProfile = useCreateDeliveryProfile();
  const updateProfile = useUpdateDeliveryProfile();
  const deleteProfile = useDeleteDeliveryProfile();
  const duplicateProfile = useDuplicateDeliveryProfile();
  const setProfileCapabilities = useSetProfileCapabilities();
  const saveDeliveryPlan = useSaveProfileDeliveryPlan();
  const restoreVersion = useRestoreProfileVersion();

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState<ProfileSubTabId>('delivery-plan');

  // Auto-select first profile on load
  useEffect(() => {
    if (profilesData?.profiles && !selectedProfileId && profilesData.profiles.length > 0) {
      setSelectedProfileId(profilesData.profiles[0].id);
    }
  }, [profilesData, selectedProfileId]);

  // Delivery plan local state
  const [planState, setPlanState] = useState<DeliveryPlanState>({
    capabilities: new Map(),
    allocations: new Map(),
    activityBaseDays: new Map(),
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize plan state when delivery plan data loads
  useEffect(() => {
    if (deliveryPlanData && workstreamsData?.workstreams) {
      const capabilities = new Map<string, { isIncluded: boolean; startWeek: number }>();
      const allocations = new Map<string, Map<number, number>>();
      const activityBaseDays = new Map<string, number>();

      // Initialize capabilities from profile data
      for (const cap of deliveryPlanData.capabilities || []) {
        capabilities.set(cap.capabilityId, {
          isIncluded: cap.isIncluded,
          startWeek: cap.startWeek,
        });
      }

      // Initialize all capabilities from workstreams that aren't in the profile (default to included)
      for (const ws of workstreamsData.workstreams) {
        for (const cap of ws.capabilities || []) {
          if (!capabilities.has(cap.id)) {
            capabilities.set(cap.id, { isIncluded: true, startWeek: 1 });
          }
        }
      }

      // Initialize allocations from profile data. Backend returns days
      // in centdays; normalise to decimal here so the Map holds the same
      // unit as the Generate output and the grid inputs (step=0.5, max=5).
      for (const alloc of deliveryPlanData.allocations || []) {
        if (!allocations.has(alloc.activityId)) {
          allocations.set(alloc.activityId, new Map());
        }
        allocations.get(alloc.activityId)!.set(alloc.week, alloc.days / 100);
      }

      // Initialize activity base days
      for (const act of deliveryPlanData.activities || []) {
        activityBaseDays.set(act.activityId, act.baseDays);
      }

      setPlanState({ capabilities, allocations, activityBaseDays });
      setHasChanges(false);
      setLastSavedAt(null);
    }
  }, [deliveryPlanData, workstreamsData]);

  // Auto-save with 30-second debounce
  useEffect(() => {
    if (hasChanges && !isSaving && selectedProfileId) {
      // Clear any existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      // Set new timer
      autoSaveTimerRef.current = setTimeout(() => {
        handleSave();
      }, 30000);
    }
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasChanges, isSaving, selectedProfileId]);

  // UI state
  const [expandedWorkstreams, setExpandedWorkstreams] = useState<Set<string>>(new Set());
  const [expandedCapabilities, setExpandedCapabilities] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileDescription, setNewProfileDescription] = useState('');
  const [editingProfileDetails, setEditingProfileDetails] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const profiles = profilesData?.profiles || [];
  const workstreams = workstreamsData?.workstreams || [];
  const selectedProfile = selectedProfileData?.profile;
  const versions = versionsData?.versions || [];
  const isLoading = profilesLoading || workstreamsLoading;

  // Toggle workstream expansion
  const toggleWorkstream = useCallback((wsId: string) => {
    setExpandedWorkstreams((prev) => {
      const next = new Set(prev);
      if (next.has(wsId)) {
        next.delete(wsId);
      } else {
        next.add(wsId);
      }
      return next;
    });
  }, []);

  // Toggle capability expansion
  const toggleCapability = useCallback((capId: string) => {
    setExpandedCapabilities((prev) => {
      const next = new Set(prev);
      if (next.has(capId)) {
        next.delete(capId);
      } else {
        next.add(capId);
      }
      return next;
    });
  }, []);

  // Expand all workstreams
  const expandAll = useCallback(() => {
    const allWsIds = new Set(workstreams.map((ws) => ws.id));
    const allCapIds = new Set(
      workstreams.flatMap((ws) => ws.capabilities?.map((cap) => cap.id) || [])
    );
    setExpandedWorkstreams(allWsIds);
    setExpandedCapabilities(allCapIds);
  }, [workstreams]);

  // Collapse all
  const collapseAll = useCallback(() => {
    setExpandedWorkstreams(new Set());
    setExpandedCapabilities(new Set());
  }, []);

  // Toggle capability inclusion
  const toggleCapabilityInclusion = useCallback((capabilityId: string) => {
    setPlanState((prev) => {
      const capabilities = new Map(prev.capabilities);
      const current = capabilities.get(capabilityId) || { isIncluded: true, startWeek: 1 };
      capabilities.set(capabilityId, { ...current, isIncluded: !current.isIncluded });
      return { ...prev, capabilities };
    });
    setHasChanges(true);
  }, []);

  // Update capability start week
  const updateCapabilityStartWeek = useCallback((capabilityId: string, startWeek: number) => {
    setPlanState((prev) => {
      const capabilities = new Map(prev.capabilities);
      const current = capabilities.get(capabilityId) || { isIncluded: true, startWeek: 1 };
      capabilities.set(capabilityId, { ...current, startWeek });
      return { ...prev, capabilities };
    });
    setHasChanges(true);
  }, []);

  // Update activity base days
  const updateActivityBaseDays = useCallback((activityId: string, baseDays: number) => {
    setPlanState((prev) => {
      const activityBaseDays = new Map(prev.activityBaseDays);
      activityBaseDays.set(activityId, baseDays);
      return { ...prev, activityBaseDays };
    });
    setHasChanges(true);
  }, []);

  // Update week allocation
  const updateWeekAllocation = useCallback((activityId: string, week: number, days: number) => {
    setPlanState((prev) => {
      const allocations = new Map(prev.allocations);
      if (!allocations.has(activityId)) {
        allocations.set(activityId, new Map());
      }
      const activityAllocs = new Map(allocations.get(activityId)!);
      if (days > 0) {
        activityAllocs.set(week, days);
      } else {
        activityAllocs.delete(week);
      }
      allocations.set(activityId, activityAllocs);
      return { ...prev, allocations };
    });
    setHasChanges(true);
  }, []);

  // Get activity base days (from profile or original)
  const getActivityBaseDays = useCallback((activity: VcfActivity): number => {
    return planState.activityBaseDays.get(activity.id) ?? activity.baseDays;
  }, [planState.activityBaseDays]);

  // Framework baseline lookup maps. Built once per delivery plan fetch
  // and used by the override visualisation below to compare each cell
  // against the framework value.
  //
  // Framework side of the comparison is in CENTDAYS (how the backend
  // returns it). Profile side (planState.allocations) is in DECIMAL
  // days after Commit 3's load-path fix. We divide framework by 100
  // at comparison time.
  const frameworkBaseline = deliveryPlanData?.framework;
  const frameworkActivityBaseDays = useMemo(() => {
    const map = new Map<string, number>(); // activityId -> centdays
    for (const act of frameworkBaseline?.activities || []) {
      map.set(act.activityId, act.baseDays);
    }
    return map;
  }, [frameworkBaseline]);
  const frameworkWeekAllocations = useMemo(() => {
    const map = new Map<string, Map<number, number>>(); // activityId -> week -> centdays
    for (const alloc of frameworkBaseline?.allocations || []) {
      if (!map.has(alloc.activityId)) {
        map.set(alloc.activityId, new Map());
      }
      map.get(alloc.activityId)!.set(alloc.week, alloc.days);
    }
    return map;
  }, [frameworkBaseline]);

  // Per-activity override check: does this activity have any rows in
  // the profile overlay that differ from framework (either in
  // allocations or in base days)? Used to drive the "Modified" badge
  // and the yellow row styling.
  const isActivityOverridden = useCallback((activity: VcfActivity): boolean => {
    // Base days override
    const profileBase = planState.activityBaseDays.get(activity.id);
    if (profileBase !== undefined) {
      const frameworkBase = frameworkActivityBaseDays.get(activity.id) ?? activity.baseDays;
      if (profileBase !== frameworkBase) return true;
    }
    // Allocation override — compare per-week values (profile decimal vs framework centdays/100)
    const profileWeeks = planState.allocations.get(activity.id);
    const frameworkWeeks = frameworkWeekAllocations.get(activity.id);
    if (profileWeeks && profileWeeks.size > 0) {
      // Any non-zero week in profile that doesn't match framework?
      let differs = false;
      for (const [week, decimal] of profileWeeks.entries()) {
        if (decimal <= 0) continue;
        const frameworkCentdays = frameworkWeeks?.get(week) ?? 0;
        if (Math.round(decimal * 100) !== frameworkCentdays) {
          differs = true;
          break;
        }
      }
      if (differs) return true;
      // Or: framework has a week the profile doesn't cover?
      if (frameworkWeeks) {
        for (const [week, centdays] of frameworkWeeks.entries()) {
          if (centdays > 0 && !(profileWeeks.get(week) ?? 0)) return true;
        }
      }
    }
    return false;
  }, [planState.activityBaseDays, planState.allocations, frameworkActivityBaseDays, frameworkWeekAllocations]);

  // Per-cell override check: does this specific (activity, week) cell
  // differ from framework? Used for the yellow cell outline.
  const isCellOverridden = useCallback((activityId: string, week: number): boolean => {
    const profileDecimal = planState.allocations.get(activityId)?.get(week) ?? 0;
    const frameworkCentdays = frameworkWeekAllocations.get(activityId)?.get(week) ?? 0;
    // If neither has a value, it's not overridden.
    if (profileDecimal === 0 && frameworkCentdays === 0) return false;
    return Math.round(profileDecimal * 100) !== frameworkCentdays;
  }, [planState.allocations, frameworkWeekAllocations]);

  // Clear all profile overrides for a capability: reset each activity
  // in the capability to the framework values (base days + weekly
  // allocations). Per-activity pruning on the backend will then drop
  // the matching rows so the profile inherits cleanly.
  const clearCapabilityOverrides = useCallback((cap: VcfCapability) => {
    if (!window.confirm(`Clear all overrides for "${cap.name}"? Activities will inherit framework values.`)) return;
    setPlanState((prev) => {
      const newAllocs = new Map(prev.allocations);
      const newBaseDays = new Map(prev.activityBaseDays);
      for (const act of cap.activities || []) {
        // Reset base days to framework (remove the override entirely).
        newBaseDays.delete(act.id);
        // Reset weekly allocations to framework values.
        const frameworkWeeks = frameworkWeekAllocations.get(act.id);
        if (frameworkWeeks && frameworkWeeks.size > 0) {
          const weekMap = new Map<number, number>();
          frameworkWeeks.forEach((centdays: number, week: number) => {
            weekMap.set(week, centdays / 100);
          });
          newAllocs.set(act.id, weekMap);
        } else {
          newAllocs.delete(act.id);
        }
      }
      return { ...prev, allocations: newAllocs, activityBaseDays: newBaseDays };
    });
    setHasChanges(true);
  }, [frameworkWeekAllocations]);

  // Get week allocation for an activity
  const getWeekAllocation = useCallback((activityId: string, week: number): number => {
    return planState.allocations.get(activityId)?.get(week) ?? 0;
  }, [planState.allocations]);

  // Get total allocated days for an activity
  const getActivityAllocatedDays = useCallback((activityId: string): number => {
    const activityAllocs = planState.allocations.get(activityId);
    if (!activityAllocs) return 0;
    let total = 0;
    activityAllocs.forEach((days) => {
      total += days;
    });
    return total;
  }, [planState.allocations]);

  // Check if capability is included
  const isCapabilityIncluded = useCallback((capabilityId: string): boolean => {
    return planState.capabilities.get(capabilityId)?.isIncluded ?? true;
  }, [planState.capabilities]);

  // Get capability start week
  const getCapabilityStartWeek = useCallback((capabilityId: string): number => {
    return planState.capabilities.get(capabilityId)?.startWeek ?? 1;
  }, [planState.capabilities]);

  // Calculate workstream totals
  const getWorkstreamTotals = useCallback((workstream: VcfWorkstream) => {
    let totalDays = 0;
    let activityCount = 0;
    let includedCapabilities = 0;

    workstream.capabilities?.forEach((cap) => {
      if (isCapabilityIncluded(cap.id)) {
        includedCapabilities++;
        cap.activities?.forEach((act) => {
          totalDays += getActivityBaseDays(act);
          activityCount++;
        });
      }
    });

    return { totalDays, activityCount, includedCapabilities, totalCapabilities: workstream.capabilities?.length || 0 };
  }, [isCapabilityIncluded, getActivityBaseDays]);

  // Calculate week totals for a capability
  const getCapabilityWeekTotals = useCallback((capability: VcfCapability): Record<number, number> => {
    const weekTotals: Record<number, number> = {};
    capability.activities?.forEach((act) => {
      planState.allocations.get(act.id)?.forEach((days, week) => {
        weekTotals[week] = (weekTotals[week] || 0) + days;
      });
    });
    return weekTotals;
  }, [planState.allocations]);

  // Save delivery plan
  const handleSave = async () => {
    if (!selectedProfileId || isSaving) return;

    setIsSaving(true);

    try {
      // Build allocations for save
      const allocations: Array<{ activityId: string; weekAllocations: Record<string, number> }> = [];
      const activities: Array<{ activityId: string; baseDays: number }> = [];

      // Collect all activities and their allocations
      for (const ws of workstreams) {
        for (const cap of ws.capabilities || []) {
          for (const act of cap.activities || []) {
            // Add activity base days
            const baseDays = planState.activityBaseDays.get(act.id) ?? act.baseDays;
            activities.push({ activityId: act.id, baseDays });

            // Add allocations
            const activityAllocs = planState.allocations.get(act.id);
            if (activityAllocs && activityAllocs.size > 0) {
              const weekAllocations: Record<string, number> = {};
              activityAllocs.forEach((days, week) => {
                weekAllocations[week.toString()] = days;
              });
              allocations.push({ activityId: act.id, weekAllocations });
            }
          }
        }
      }

      // Save capabilities
      const capabilitiesToSave = Array.from(planState.capabilities.entries()).map(([capabilityId, data]) => ({
        capabilityId,
        isIncluded: data.isIncluded,
        startWeek: data.startWeek,
      }));

      await setProfileCapabilities.mutateAsync({
        id: selectedProfileId,
        capabilities: capabilitiesToSave,
      });

      // Save delivery plan (allocations + activities)
      await saveDeliveryPlan.mutateAsync({
        id: selectedProfileId,
        allocations,
        activities,
        changeDescription: 'Updated delivery plan',
      });

      setHasChanges(false);
      setLastSavedAt(new Date());
    } catch (error) {
      console.error('Failed to save profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    if (!confirm('Discard all unsaved changes?')) return;
    // Reset by re-initializing from current data
    if (deliveryPlanData && workstreamsData?.workstreams) {
      const capabilities = new Map<string, { isIncluded: boolean; startWeek: number }>();
      const allocations = new Map<string, Map<number, number>>();
      const activityBaseDays = new Map<string, number>();

      for (const cap of deliveryPlanData.capabilities || []) {
        capabilities.set(cap.capabilityId, {
          isIncluded: cap.isIncluded,
          startWeek: cap.startWeek,
        });
      }

      for (const ws of workstreamsData.workstreams) {
        for (const cap of ws.capabilities || []) {
          if (!capabilities.has(cap.id)) {
            capabilities.set(cap.id, { isIncluded: true, startWeek: 1 });
          }
        }
      }

      for (const alloc of deliveryPlanData.allocations || []) {
        if (!allocations.has(alloc.activityId)) {
          allocations.set(alloc.activityId, new Map());
        }
        // Normalise centdays → decimal (see Map type comment above).
        allocations.get(alloc.activityId)!.set(alloc.week, alloc.days / 100);
      }

      for (const act of deliveryPlanData.activities || []) {
        activityBaseDays.set(act.activityId, act.baseDays);
      }

      setPlanState({ capabilities, allocations, activityBaseDays });
      setHasChanges(false);
    }
  };

  // Create new profile
  const handleCreateProfile = async () => {
    if (!newProfileName.trim()) return;

    try {
      const result = await createProfile.mutateAsync({
        name: newProfileName.trim(),
        description: newProfileDescription.trim() || undefined,
      });
      setSelectedProfileId(result.profile.id);
      setShowCreateModal(false);
      setNewProfileName('');
      setNewProfileDescription('');
    } catch (error) {
      console.error('Failed to create profile:', error);
    }
  };

  // Clone profile
  const handleCloneProfile = async (profileId: string, profileName: string) => {
    const newName = prompt('Enter name for the cloned profile:', `${profileName} (Copy)`);
    if (!newName) return;

    try {
      const result = await duplicateProfile.mutateAsync({
        id: profileId,
        name: newName,
      });
      setSelectedProfileId(result.profile.id);
    } catch (error) {
      console.error('Failed to clone profile:', error);
    }
  };

  // Delete profile
  const handleDeleteProfile = async (profileId: string) => {
    if (!confirm('Are you sure you want to delete this service package?')) return;

    try {
      await deleteProfile.mutateAsync(profileId);
      if (selectedProfileId === profileId) {
        setSelectedProfileId(null);
      }
    } catch (error) {
      console.error('Failed to delete profile:', error);
    }
  };

  // Update profile details
  const handleSaveProfileDetails = async () => {
    if (!selectedProfileId) return;

    try {
      await updateProfile.mutateAsync({
        id: selectedProfileId,
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      setEditingProfileDetails(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  // Restore version
  const handleRestoreVersion = async (versionId: string, versionNumber: number) => {
    if (!selectedProfileId) return;
    if (!confirm(`Restore to version ${versionNumber}? Current state will be saved as a new version.`)) return;

    try {
      await restoreVersion.mutateAsync({
        profileId: selectedProfileId,
        versionId,
      });
    } catch (error) {
      console.error('Failed to restore version:', error);
    }
  };

  // Start editing profile details
  const startEditingDetails = () => {
    if (selectedProfile) {
      setEditName(selectedProfile.name);
      setEditDescription(selectedProfile.description || '');
      setEditingProfileDetails(true);
    }
  };

  // Format time ago
  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-[calc(100vh-220px)] min-h-[500px]">
      {/* Left Panel - Service Package List */}
      <div className="w-72 flex-shrink-0 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">Packages</h3>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary btn-sm"
            >
              <Plus className="h-3 w-3 mr-1" />
              New
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Select a package to configure
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {profiles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No packages yet</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-secondary btn-sm mt-2"
              >
                Create First Package
              </button>
            </div>
          ) : (
            profiles.map((profile) => (
              <div
                key={profile.id}
                className={cn(
                  'p-3 rounded-lg cursor-pointer transition-colors group',
                  selectedProfileId === profile.id
                    ? 'bg-blue-50 border border-blue-200'
                    : 'hover:bg-gray-50 border border-transparent'
                )}
                onClick={() => setSelectedProfileId(profile.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">
                        {profile.name}
                      </span>
                    </div>
                    {profile.description && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {profile.description}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1">
                      v{profile.version} · {new Date(profile.updatedAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloneProfile(profile.id, profile.name);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Clone package"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProfile(profile.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Delete package"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Profile Editor */}
      <div className="flex-1 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden">
        {!selectedProfileId || profileLoading ? (
          <div className="flex-1 flex items-center justify-center">
            {profileLoading ? (
              <LoadingSpinner size="lg" />
            ) : (
              <div className="text-center text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Select a profile to edit</p>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Profile Editor Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">
                        {selectedProfile?.name || 'Untitled'}
                      </h3>
                      <span className="text-xs text-gray-400">v{selectedProfile?.version}</span>
                    </div>
                    {selectedProfile?.description && (
                      <p className="text-sm text-gray-500 mt-0.5">
                        {selectedProfile.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {/* Save Status */}
                  <span className={cn(
                    'text-xs flex items-center gap-1',
                    isSaving && 'text-blue-600',
                    hasChanges && !isSaving && 'text-amber-600',
                    !hasChanges && lastSavedAt && 'text-green-600',
                    !hasChanges && !lastSavedAt && 'text-gray-400'
                  )}>
                    {isSaving ? (
                      <>◐ Saving...</>
                    ) : hasChanges ? (
                      <>● Unsaved changes</>
                    ) : lastSavedAt ? (
                      <>✓ Saved {formatTimeAgo(lastSavedAt)}</>
                    ) : (
                      <>✓ No changes</>
                    )}
                  </span>

                  {hasChanges && (
                    <button onClick={handleDiscard} className="btn-secondary btn-sm">
                      Discard
                    </button>
                  )}
                  <button
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                    className={cn(
                      'btn-primary',
                      (!hasChanges || isSaving) && 'opacity-50 cursor-not-allowed'
                    )}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              {/* Sub-tabs */}
              <div className="flex gap-1 mt-4 border-b border-gray-200 -mb-4">
                {PROFILE_SUB_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSubTab(tab.id)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2',
                      activeSubTab === tab.id
                        ? 'border-xtravirt-cyan text-xtravirt-cyan'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    )}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sub-tab Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Profile Details Tab */}
              {activeSubTab === 'details' && (
                <div className="p-6 max-w-2xl">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Package Details</h4>
                  {editingProfileDetails ? (
                    <div className="space-y-4">
                      <div>
                        <label className="label">Name</label>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="input mt-1 w-full"
                        />
                      </div>
                      <div>
                        <label className="label">Description</label>
                        <textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          className="input mt-1 w-full"
                          rows={3}
                        />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={handleSaveProfileDetails} className="btn-primary">
                          Save Details
                        </button>
                        <button onClick={() => setEditingProfileDetails(false)} className="btn-secondary">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="label">Name</label>
                        <p className="text-gray-900 mt-1">{selectedProfile?.name}</p>
                      </div>
                      <div>
                        <label className="label">Description</label>
                        <p className="text-gray-900 mt-1">{selectedProfile?.description || <span className="text-gray-400 italic">No description</span>}</p>
                      </div>
                      <div>
                        <label className="label">Version</label>
                        <p className="text-gray-900 mt-1">{selectedProfile?.version}</p>
                      </div>
                      <div>
                        <label className="label">Last Updated</label>
                        <p className="text-gray-900 mt-1">{selectedProfile?.updatedAt ? new Date(selectedProfile.updatedAt).toLocaleString() : 'N/A'}</p>
                      </div>
                      <button onClick={startEditingDetails} className="btn-secondary">
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit Details
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Plan Tab */}
              {activeSubTab === 'delivery-plan' && (
                <div className="p-4">
                  {deliveryPlanLoading ? (
                    <div className="flex justify-center py-12">
                      <LoadingSpinner size="lg" />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <button onClick={expandAll} className="btn-secondary btn-sm">
                            Expand All
                          </button>
                          <button onClick={collapseAll} className="btn-secondary btn-sm">
                            Collapse All
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {workstreams.map((ws) => {
                          const isExpanded = expandedWorkstreams.has(ws.id);
                          const totals = getWorkstreamTotals(ws);

                          return (
                            <div
                              key={ws.id}
                              className="card overflow-hidden !p-0"
                            >
                              {/* Workstream Header */}
                              <div
                                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                                onClick={() => toggleWorkstream(ws.id)}
                              >
                                <div className="flex items-center gap-3">
                                  {isExpanded ? (
                                    <ChevronDown className="h-5 w-5 text-gray-400" />
                                  ) : (
                                    <ChevronRight className="h-5 w-5 text-gray-400" />
                                  )}
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-mono text-sm text-xtravirt-cyan font-medium">
                                        {ws.code}
                                      </span>
                                      <span className="font-semibold text-xtravirt-dark">
                                        {ws.name}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      {totals.includedCapabilities}/{totals.totalCapabilities} capabilities · {totals.activityCount} activities · {(totals.totalDays / 100).toFixed(1)} days
                                    </div>
                                  </div>
                                </div>
                                {ws.phase && (
                                  <span className="badge badge-gray text-xs capitalize">
                                    {ws.phase}
                                  </span>
                                )}
                              </div>

                              {/* Expanded Content */}
                              {isExpanded && (
                                <div className="border-t border-gray-100">
                                  {ws.capabilities?.map((cap) => {
                                    const isCapExpanded = expandedCapabilities.has(cap.id);
                                    const included = isCapabilityIncluded(cap.id);
                                    const startWeek = getCapabilityStartWeek(cap.id);
                                    const weekTotals = getCapabilityWeekTotals(cap);

                                    return (
                                      <div key={cap.id} className={cn(
                                        'border-b border-gray-100 last:border-b-0',
                                        !included && 'opacity-50'
                                      )}>
                                        {/* Capability Header */}
                                        <div className="dp-capability-header">
                                          <div className="flex items-center gap-3">
                                            {/* Inclusion checkbox */}
                                            <input
                                              type="checkbox"
                                              checked={included}
                                              onChange={() => toggleCapabilityInclusion(cap.id)}
                                              onClick={(e) => e.stopPropagation()}
                                              className="h-4 w-4 rounded border-gray-300 text-xtravirt-cyan focus:ring-xtravirt-cyan"
                                            />
                                            <div
                                              className="flex items-center gap-2 cursor-pointer flex-1"
                                              onClick={() => toggleCapability(cap.id)}
                                            >
                                              {isCapExpanded ? (
                                                <ChevronDown className="h-4 w-4 text-gray-400" />
                                              ) : (
                                                <ChevronRight className="h-4 w-4 text-gray-400" />
                                              )}
                                              <div>
                                                <div className="flex items-center gap-2">
                                                  <span className="dp-capability-code">{cap.code}</span>
                                                  <span className="dp-capability-name">{cap.name}</span>
                                                  {cap.cls && (
                                                    <span className={cn(
                                                      'px-1.5 py-0.5 text-xs font-medium rounded',
                                                      cap.cls === 'CORE' && 'bg-blue-100 text-blue-700',
                                                      cap.cls === 'RECOMMENDED' && 'bg-green-100 text-green-700',
                                                      cap.cls === 'OPTIONAL' && 'bg-gray-100 text-gray-600'
                                                    )}>
                                                      {cap.cls}
                                                    </span>
                                                  )}
                                                </div>
                                                <div className="dp-capability-meta">
                                                  {cap.activities?.length || 0} activities
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          {included && (
                                            <div className="flex items-center gap-2">
                                              <label className="text-xs text-gray-500">Start:</label>
                                              <select
                                                value={startWeek}
                                                onChange={(e) => updateCapabilityStartWeek(cap.id, parseInt(e.target.value))}
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-xs border border-gray-300 rounded px-2 py-1"
                                              >
                                                {Array.from({ length: NUM_WEEKS }, (_, i) => (
                                                  <option key={i + 1} value={i + 1}>W{i + 1}</option>
                                                ))}
                                              </select>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (!window.confirm('Generate will auto-schedule week allocations from base days. Continue?')) return;
                                                  // Generate allocations for this capability using shared utility
                                                  const acts = (cap.activities || []).map(act => ({
                                                    id: act.id,
                                                    role: act.role,
                                                    skill: act.skill || '',
                                                    adjustedDays: getActivityBaseDays(act) / 100,
                                                    phase: act.activityPhase as import('@/hooks/useWorkstreams').ActivityPhase | undefined,
                                                    sortOrder: act.sortOrder,
                                                  }));
                                                  const result = generateWeekAllocations(acts, startWeek, DAYS_PER_WEEK, NUM_WEEKS);
                                                  setPlanState(prev => {
                                                    const newAllocs = new Map(prev.allocations);
                                                    for (const act of cap.activities || []) {
                                                      const weekMap = new Map<number, number>();
                                                      for (const [w, d] of Object.entries(result.allocations[act.id] || {})) {
                                                        weekMap.set(Number(w), d);
                                                      }
                                                      newAllocs.set(act.id, weekMap);
                                                    }
                                                    return { ...prev, allocations: newAllocs };
                                                  });
                                                  setHasChanges(true);
                                                }}
                                                className="btn-secondary btn-sm text-xs"
                                              >
                                                Generate
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  clearCapabilityOverrides(cap);
                                                }}
                                                className="btn-secondary btn-sm text-xs"
                                                title="Reset all activities in this capability to framework values"
                                              >
                                                Clear overrides
                                              </button>
                                            </div>
                                          )}
                                        </div>

                                        {/* Activities Table — mirrors project delivery plan layout */}
                                        {isCapExpanded && included && (
                                          <div className="p-4">
                                            <div className="overflow-x-auto">
                                              <table className="dp-table">
                                                <thead>
                                                  <tr>
                                                    <th className="text-left min-w-[180px]">Activity</th>
                                                    <th className="text-left w-20">Phase</th>
                                                    <th className="text-left w-24">Skill</th>
                                                    <th className="text-left w-32">Role</th>
                                                    <th className="text-center w-14">Base</th>
                                                    <th className="text-center w-14">LOE</th>
                                                    {Array.from({ length: NUM_WEEKS }, (_, i) => (
                                                      <th key={i} className="dp-week-header">W{i + 1}</th>
                                                    ))}
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {cap.activities?.map((act) => {
                                                    const baseDays = getActivityBaseDays(act);
                                                    const allocatedDays = getActivityAllocatedDays(act.id);
                                                    const expectedDays = baseDays / 100;
                                                    const hasMismatch = allocatedDays > 0 && Math.abs(allocatedDays - expectedDays) > 0.01;
                                                    // Under Level 2 overlay semantics: an activity is "modified"
                                                    // if any of its values differ from the framework baseline
                                                    // (either base days or any week cell).
                                                    const isModified = isActivityOverridden(act);
                                                    const skillColour = adminGetSkillColours(act.skill, skillsList);
                                                    const phase = act.activityPhase;

                                                    return (
                                                      <tr key={act.id} className={cn(
                                                        isModified && 'dp-row-modified',
                                                        hasMismatch && 'dp-row-mismatch'
                                                      )}>
                                                        <td>
                                                          <div className="flex items-center gap-2">
                                                            {act.isMilestone ? (
                                                              <span className="text-amber-500">◆</span>
                                                            ) : null}
                                                            <span className="dp-activity-name">{act.name}</span>
                                                            {isModified && (
                                                              <span className="dp-activity-badge modified">Modified</span>
                                                            )}
                                                          </div>
                                                        </td>
                                                        <td>
                                                          {phase && ADMIN_PHASE_COLOURS[phase] && (
                                                            <span
                                                              className="inline-block px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider"
                                                              style={{ backgroundColor: ADMIN_PHASE_COLOURS[phase].bg, color: ADMIN_PHASE_COLOURS[phase].text }}
                                                            >
                                                              {ADMIN_PHASE_COLOURS[phase].label}
                                                            </span>
                                                          )}
                                                        </td>
                                                        <td>
                                                          {act.skill && (
                                                            <span
                                                              className="inline-block px-2.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                                                              style={{ backgroundColor: skillColour.bg, color: skillColour.text }}
                                                            >
                                                              {act.skill}
                                                            </span>
                                                          )}
                                                        </td>
                                                        <td>
                                                          {(() => {
                                                            const rc = adminGetRoleColours(act.role, rolesList);
                                                            return (
                                                              <span
                                                                className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider"
                                                                style={{ backgroundColor: rc.bg, color: rc.text }}
                                                              >
                                                                {act.role}
                                                              </span>
                                                            );
                                                          })()}
                                                        </td>
                                                        <td className="text-center text-xs text-gray-400 tabular-nums">
                                                          {(act.baseDays / 100).toFixed(1)}
                                                        </td>
                                                        <td className="text-center">
                                                          <input
                                                            type="number"
                                                            value={(baseDays / 100).toFixed(1)}
                                                            onChange={(e) => updateActivityBaseDays(act.id, Math.round(parseFloat(e.target.value || '0') * 100))}
                                                            className={cn('dp-days-input', isModified && 'dp-days-modified')}
                                                            step="0.5"
                                                            min="0"
                                                          />
                                                        </td>
                                                        {Array.from({ length: NUM_WEEKS }, (_, i) => {
                                                          const week = i + 1;
                                                          const days = getWeekAllocation(act.id, week);
                                                          const overridden = isCellOverridden(act.id, week);
                                                          return (
                                                            <td key={i} className="dp-week-cell">
                                                              <input
                                                                type="number"
                                                                value={days || ''}
                                                                onChange={(e) => updateWeekAllocation(act.id, week, parseFloat(e.target.value) || 0)}
                                                                className={cn(
                                                                  'dp-week-input',
                                                                  days > 0 && 'dp-cell-filled',
                                                                  overridden && 'dp-cell-overridden'
                                                                )}
                                                                step="0.5"
                                                                min="0"
                                                                max={DAYS_PER_WEEK}
                                                                title={overridden ? 'Overrides framework baseline' : undefined}
                                                              />
                                                            </td>
                                                          );
                                                        })}
                                                      </tr>
                                                    );
                                                  })}
                                                  {/* Week Totals Row */}
                                                  <tr className="dp-totals-row">
                                                    <td colSpan={6} className="text-right pr-4">Week Totals</td>
                                                    {Array.from({ length: NUM_WEEKS }, (_, i) => {
                                                      const week = i + 1;
                                                      const total = weekTotals[week] || 0;
                                                      return (
                                                        <td key={i} className={total > DAYS_PER_WEEK ? 'dp-total-over' : ''}>
                                                          {total > 0 ? total.toFixed(1) : ''}
                                                        </td>
                                                      );
                                                    })}
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Version History Tab */}
              {activeSubTab === 'versions' && (
                <div className="p-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Version History</h4>
                  {versions.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <History className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No version history yet</p>
                      <p className="text-sm mt-1">Changes will be tracked when you save</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {versions.map((version) => (
                        <div
                          key={version.id}
                          className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">Version {version.version}</span>
                                {version.version === selectedProfile?.version && (
                                  <span className="badge badge-green text-xs">Current</span>
                                )}
                              </div>
                              {version.changeDescription && (
                                <p className="text-sm text-gray-600 mt-1">{version.changeDescription}</p>
                              )}
                              <div className="text-xs text-gray-500 mt-2">
                                {new Date(version.createdAt).toLocaleString()}
                                {version.createdByEmail && ` · ${version.createdByEmail}`}
                              </div>
                              {version.changedFields && version.changedFields.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {version.changedFields.map((field, idx) => (
                                    <span key={idx} className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                      {field}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            {version.version !== selectedProfile?.version && (
                              <button
                                onClick={() => handleRestoreVersion(version.id, version.version)}
                                className="btn-secondary btn-sm"
                                disabled={restoreVersion.isPending}
                              >
                                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                                Restore
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Service Package Modal */}
      {showCreateModal && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h2 className="dialog-title">Create New Package</h2>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateProfile();
              }}
              className="dialog-body space-y-4"
            >
              <div>
                <label className="label">Package Name *</label>
                <input
                  type="text"
                  value={newProfileName}
                  onChange={(e) => setNewProfileName(e.target.value)}
                  className="input mt-1 w-full"
                  placeholder="e.g., Standard VCF 5 Package"
                  required
                />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea
                  value={newProfileDescription}
                  onChange={(e) => setNewProfileDescription(e.target.value)}
                  className="input mt-1 w-full"
                  rows={3}
                  placeholder="Optional description for this package"
                />
              </div>
            </form>
            <div className="dialog-footer">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewProfileName('');
                  setNewProfileDescription('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProfile}
                disabled={!newProfileName.trim() || createProfile.isPending}
                className="btn-primary"
              >
                {createProfile.isPending ? 'Creating...' : 'Create Package'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
